import {
  applyAdminUserTimeout,
  clearAdminUserTimeout,
  createAdminTempPassword,
  deleteAdminLeaderboardEntry,
  deleteAllAdminLeaderboardEntries,
  deleteAdminUserStats,
  deleteAdminPlayerAchievement,
  deleteAdminPlayerStat,
  deleteAdminPlayerSyncedRecord,
  fetchAdminActiveTimeoutUsers,
  fetchAdminGrantUsers,
  fetchAdminLeaderboardEntries,
  fetchAdminPasswordRecoveryUsers,
  fetchAdminPlayerStats,
  fetchAdminTimeoutUsers,
  lookupAdminPlayerStats,
  patchAdminPlayerStats,
  patchAdminPlayerDots,
  removeAdminPlayerPurchase,
  type AdminPlayerStatsDetailApi,
  patchAdminUserPermissions,
  type AdminPermissionKey,
  type AdminTimeoutAction,
} from "../lib/cloud";
import { mountAdminAchievements } from "./admin-achievements-ui";
import { mountAdminTasks } from "./admin-tasks-ui";
import {
  formatTimeoutDurationLabel,
  msToDurationToken,
  parseTimeoutDuration,
} from "../../lib/timeoutDuration";
import { mountAdminSpeedrunEditor } from "./admin-speedrun-editor";
import { mountAdminSpeedrunLevelsList } from "./admin-speedrun-levels-ui";
import { mountAdminPenaltyEditor } from "./admin-penalty-editor";
import { mountAdminPenaltyLevelsList } from "./admin-penalty-levels-ui";
import { mountAdminPracticeEditor, type AdminPracticeEditorApi } from "./admin-practice-editor";
import { mountAdminPracticeLevelsList } from "./admin-practice-levels-ui";
import { setPracticeHubVisible } from "./game-hub";
import { claimExclusiveUi } from "./exclusive-ui";
import { mountAdminWallEditor } from "./admin-wall-editor";
import {
  mountAdminCrateCreator,
  mountAdminShopCreator,
  type AdminShopCreatorApi,
} from "./admin-shop-creator";
import { mountAdminShopItems } from "./admin-shop-items";
import { mountAdminShopCrates } from "./admin-shop-crates";
import { mountAdminSplashEditor } from "./admin-splash-ui";
import { mountAdminChallenges } from "./admin-challenges-ui";
import { mountAdminTournaments } from "./admin-tournaments-ui";
import { mountAdminCampaign } from "./admin-campaign-ui";
import { mountAdminMarathon } from "./admin-marathon-ui";
import { mountAdminAnnouncements } from "./admin-announcements-ui";
import { mountAdminWhatsNew } from "./admin-whats-new-ui";
import { mountAdminBattlePass } from "./admin-battle-pass-ui";
import { mountAdminThemeCreator } from "./admin-theme-creator";
import { mountAdminIconCreator } from "./admin-icon-creator";
import type { ThemeData } from "./theme-types";
import type { ThemeCosmeticData } from "./theme-cosmetic";

const ADMIN_PERMISSION_ORDER: AdminPermissionKey[] = [
    "records",
    "passwordRecovery",
    "speedrunLevels",
    "speedrunEditor",
    "speedrunPublish",
    "speedrunOverwrite",
    "penaltyLevels",
    "penaltyEditor",
    "penaltyPublish",
    "penaltyOverwrite",
    "practiceLevels",
    "practiceEditor",
    "practicePublish",
    "practiceOverwrite",
    "wallEdit",
    "wallPublish",
    "wallDelete",
    "achievements",
    "shopCreator",
    "shopItems",
    "shopOverwrite",
    "shopDelete",
    "shopDots",
    "crates",
    "crateCreator",
    "cratesPublish",
    "cratesOverwrite",
    "splashEdit",
    "challenges",
    "tournamentsEditor",
    "marathon",
    "campaign",
    "tasksEditor",
    "battlePassEditor",
    "announcementsSend",
    "whatsNew",
    "themeEditor",
    "iconEditor",
    "administrators",
];

const ADMIN_PERMISSION_LABELS: Record<AdminPermissionKey, string> = {
    records: "Moderation",
    passwordRecovery: "Password Recovery",
    speedrunLevels: "SpeedRun Levels",
    speedrunEditor: "SpeedRun Editor",
    speedrunPublish: "Publish Speedrun Levels",
    speedrunOverwrite: "Overwrite Levels",
    penaltyLevels: "Golf Levels",
    penaltyEditor: "Golf Editor",
    penaltyPublish: "Publish Golf Levels",
    penaltyOverwrite: "Overwrite Golf Levels",
    practiceLevels: "Practice Levels",
    practiceEditor: "Practice Creator",
    practicePublish: "Publish Practice Levels",
    practiceOverwrite: "Overwrite Practice Levels",
    wallEdit: "Wall — Edit",
    wallPublish: "Wall — Publish",
    wallDelete: "Wall — Delete history",
    achievements: "Achievements",
    shopCreator: "Shop Creator",
    shopItems: "Shop Items",
    shopOverwrite: "Overwrite Shop Items",
    shopDelete: "Delete Shop Items",
    shopDots: "Shop & Dots",
    crates: "Crates",
    crateCreator: "Crates Creator",
    cratesPublish: "Publish Crates Items",
    cratesOverwrite: "Overwrite Crates Items",
    splashEdit: "Splash",
    challenges: "Challenges",
    tournamentsEditor: "Tournaments",
    marathon: "Marathon",
    campaign: "Campaign",
    tasksEditor: "Tasks",
    battlePassEditor: "Battle Pass",
    announcementsSend: "Announcements",
    whatsNew: "What's New",
    themeEditor: "Theme Creator",
    iconEditor: "Icon Creator",
    administrators: "Administrators",
};

function formatSpeedrunTime(ms: number) {
    const m = Math.floor(ms / 60000);
    const s = (ms % 60000) / 1000;
    if (m <= 0) return s.toFixed(2) + " s";
    return `${m}m ${s < 10 ? "0" : ""}${s.toFixed(2)}s`;
}

const MODERATION_MODES_WITH_LEVELS = new Set(["speedrun"]);

function formatPlaytimeDhms(sec: number): string {
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    return `${d}d ${h}h ${m}m ${r}s`;
}

function parsePlaytimeDhms(input: string): number | null {
    const s = input.trim().toLowerCase();
    if (!s) return null;
    const dayM = /(\d+)\s*d/.exec(s);
    const hourM = /(\d+)\s*h/.exec(s);
    const minM = /(\d+)\s*m/.exec(s);
    const secM = /(\d+)\s*s/.exec(s);
    if (!dayM && !hourM && !minM && !secM) return null;
    const d = dayM ? Number(dayM[1]) : 0;
    const h = hourM ? Number(hourM[1]) : 0;
    const m = minM ? Number(minM[1]) : 0;
    const r = secM ? Number(secM[1]) : 0;
    if (![d, h, m, r].every((n) => Number.isFinite(n) && n >= 0)) return null;
    return d * 86400 + h * 3600 + m * 60 + r;
}

export function initAdminPanel(shell: {
    /** Call after panel visibility changes so sidebar / MENU toggle refresh (legacy-app uses showEl for most screens; admin uses classList directly). */
    onVisibilityChange?: () => void;
    /** Called when a signed-in player's cloud records were removed (sync local bests / stats). */
    onPlayerRecordsChanged?: (userId: string | null) => void | Promise<void>;
    screen: HTMLElement;
    btnReturn: HTMLButtonElement;
    btnRecords: HTMLButtonElement;
    btnPasswordRecovery: HTMLButtonElement;
    btnLevels: HTMLButtonElement;
    btnEditor: HTMLButtonElement;
    btnPenaltyLevels: HTMLButtonElement;
    btnPenaltyEditor: HTMLButtonElement;
    btnPracticeLevels: HTMLButtonElement;
    btnPracticeEditor: HTMLButtonElement;
    btnWall: HTMLButtonElement;
    btnAchievements: HTMLButtonElement;
    btnTasks: HTMLButtonElement;
    btnBattlePass: HTMLButtonElement;
    btnChallenges: HTMLButtonElement;
    btnTournaments: HTMLButtonElement;
    btnMarathon: HTMLButtonElement;
    btnCampaign: HTMLButtonElement;
    btnAnnouncements: HTMLButtonElement;
    btnWhatsNew: HTMLButtonElement;
    btnShopCreator: HTMLButtonElement;
    btnCrateCreator: HTMLButtonElement;
    btnShopItems: HTMLButtonElement;
    btnShopCrates: HTMLButtonElement;
    btnShopDots: HTMLButtonElement;
    btnSplash: HTMLButtonElement;
    btnThemeCreator: HTMLButtonElement;
    btnIconCreator: HTMLButtonElement;
    btnAdministrators: HTMLButtonElement;
    adminPermissions: AdminPermissionKey[];
    currentUserId: string | null;
    /** Leave admin UI and open Home for navigable theme preview. */
    onEnterThemePreview?: (api: {
        getData: () => ThemeData;
        setData: (data: ThemeData) => void;
        onExit: () => void;
    }) => void;
    /** Shop / Crates Creator Theme: navigable preview (records & Dots off). */
    onEnterThemeCosmeticPreview?: (api: {
        getData: () => ThemeCosmeticData | null;
        setData: (data: ThemeCosmeticData | null) => void;
        onExit: () => void;
        returnButtonId?: string;
    }) => void;
    /** Fired when the admin panel is fully closed back to the game. */
    onAdminClosed?: () => void;
    panelRecords: HTMLElement;
    panelPasswordRecovery: HTMLElement;
    panelLevels: HTMLElement;
    panelEditor: HTMLElement;
    panelPenaltyLevels: HTMLElement;
    panelPenaltyEditor: HTMLElement;
    panelPracticeLevels: HTMLElement;
    panelPracticeEditor: HTMLElement;
    panelWall: HTMLElement;
    panelAchievements: HTMLElement;
    panelTasks: HTMLElement;
    panelBattlePass: HTMLElement;
    panelChallenges: HTMLElement;
    panelTournaments: HTMLElement;
    panelMarathon: HTMLElement;
    panelCampaign: HTMLElement;
    panelAnnouncements: HTMLElement;
    panelWhatsNew: HTMLElement;
    panelShopCreator: HTMLElement;
    panelCrateCreator: HTMLElement;
    panelShopItems: HTMLElement;
    panelShopCrates: HTMLElement;
    panelShopDots: HTMLElement;
    panelSplash: HTMLElement;
    panelThemeCreator: HTMLElement;
    panelIconCreator: HTMLElement;
    panelAdministrators: HTMLElement;
    levelsMount: HTMLElement;
    penaltyLevelsMount: HTMLElement;
    penaltyEditorMount: HTMLElement;
    practiceLevelsMount: HTMLElement;
    practiceEditorMount: HTMLElement;
    recordsSearch: HTMLInputElement;
    recordsMode: HTMLSelectElement;
    recordsSpeedrunLevel: HTMLSelectElement;
    recordsSpeedrunRow: HTMLElement;
    recordsRefresh: HTMLButtonElement;
    recordsManageStatsBtn: HTMLButtonElement;
    recordsTableWrap: HTMLElement;
    recordsStatus: HTMLElement;
    playerStatsScreen: HTMLElement;
    playerStatsBackBtn: HTMLButtonElement;
    playerStatsTitle: HTMLElement;
    playerStatsStatus: HTMLElement;
    playerStatsCore: HTMLElement;
    playerStatsAchievements: HTMLElement;
    playerStatsRecords: HTMLElement;
    playerStatsSynced: HTMLElement;
    playerStatsChangeModal: HTMLElement;
    playerStatsChangeLabel: HTMLElement;
    playerStatsChangeCurrent: HTMLElement;
    playerStatsChangeInput: HTMLInputElement;
    playerStatsChangeHint: HTMLElement;
    playerStatsChangeError: HTMLElement;
    playerStatsChangeCancelBtn: HTMLButtonElement;
    playerStatsChangeApplyBtn: HTMLButtonElement;
    timeoutSearch: HTMLInputElement;
    timeoutMinutes: HTMLInputElement;
    timeoutSearchBtn: HTMLButtonElement;
    timeoutActiveRefreshBtn: HTMLButtonElement;
    timeoutActiveTableWrap: HTMLElement;
    timeoutActiveStatus: HTMLElement;
    timeoutTableWrap: HTMLElement;
    timeoutStatus: HTMLElement;
    timeoutReduceModal: HTMLElement;
    timeoutReduceUser: HTMLElement;
    timeoutReduceCurrent: HTMLElement;
    timeoutReduceMode: HTMLSelectElement;
    timeoutReduceDuration: HTMLInputElement;
    timeoutReduceError: HTMLElement;
    timeoutReduceCancelBtn: HTMLButtonElement;
    timeoutReduceApplyBtn: HTMLButtonElement;
    deleteRecordsModal: HTMLElement;
    deleteRecordsUser: HTMLElement;
    deleteRecordsMode: HTMLElement;
    deleteRecordsLevelList: HTMLElement;
    deleteRecordsError: HTMLElement;
    deleteRecordsCancelBtn: HTMLButtonElement;
    deleteRecordsAllLevelsBtn: HTMLButtonElement;
    passwordRecoverySearch: HTMLInputElement;
    passwordRecoverySearchBtn: HTMLButtonElement;
    passwordRecoveryTableWrap: HTMLElement;
    passwordRecoveryStatus: HTMLElement;
    passwordRecoveryResult: HTMLElement;
    tempPasswordUser: HTMLElement;
    tempPasswordEmail: HTMLElement;
    tempPasswordValue: HTMLElement;
    tempPasswordCopyBtn: HTMLButtonElement;
    editorMount: HTMLElement;
    wallMount: HTMLElement;
    achievementsMount: HTMLElement;
    tasksMount: HTMLElement;
    battlePassMount: HTMLElement;
    challengesMount: HTMLElement;
    tournamentsMount: HTMLElement;
    marathonMount: HTMLElement;
    campaignMount: HTMLElement;
    announcementsMount: HTMLElement;
    whatsNewMount: HTMLElement;
    shopCreatorMount: HTMLElement;
    crateCreatorMount: HTMLElement;
    shopItemsMount: HTMLElement;
    shopCratesMount: HTMLElement;
    shopDotsSearch: HTMLInputElement;
    shopDotsSearchBtn: HTMLButtonElement;
    shopDotsStatus: HTMLElement;
    shopDotsCandidates: HTMLElement;
    shopDotsDetail: HTMLElement;
    shopDotsDetailTitle: HTMLElement;
    shopDotsBalance: HTMLElement;
    shopDotsInventory: HTMLElement;
    splashMount: HTMLElement;
    themeCreatorMount: HTMLElement;
    iconCreatorMount: HTMLElement;
    administratorsSearch: HTMLInputElement;
    administratorsSearchBtn: HTMLButtonElement;
    administratorsListBtn: HTMLButtonElement;
    administratorsTableWrap: HTMLElement;
    administratorsStatus: HTMLElement;
    onSpeedrunLevelsChanged: () => void;
    onPenaltyLevelsChanged: () => void;
    onPracticeLevelsChanged: () => void;
}) {
    let editorApi: ReturnType<typeof mountAdminSpeedrunEditor> | null = null;
    let levelsApi: ReturnType<typeof mountAdminSpeedrunLevelsList> | null = null;
    let penaltyEditorApi: ReturnType<typeof mountAdminPenaltyEditor> | null = null;
    let penaltyLevelsApi: ReturnType<typeof mountAdminPenaltyLevelsList> | null = null;
    let practiceEditorApi: AdminPracticeEditorApi | null = null;
    let practiceLevelsApi: ReturnType<typeof mountAdminPracticeLevelsList> | null = null;
    let wallApi: ReturnType<typeof mountAdminWallEditor> | null = null;
    let achievementsApi: ReturnType<typeof mountAdminAchievements> | null = null;
    let tasksApi: ReturnType<typeof mountAdminTasks> | null = null;
    let battlePassApi: ReturnType<typeof mountAdminBattlePass> | null = null;
    let challengesApi: ReturnType<typeof mountAdminChallenges> | null = null;
    let tournamentsMounted = false;
    let campaignApi: ReturnType<typeof mountAdminCampaign> | null = null;
    let marathonApi: ReturnType<typeof mountAdminMarathon> | null = null;
    let announcementsApi: ReturnType<typeof mountAdminAnnouncements> | null = null;
    let whatsNewApi: ReturnType<typeof mountAdminWhatsNew> | null = null;
    let splashApi: ReturnType<typeof mountAdminSplashEditor> | null = null;
    let themeCreatorApi: ReturnType<typeof mountAdminThemeCreator> | null = null;
    let iconCreatorApi: ReturnType<typeof mountAdminIconCreator> | null = null;
    let shopCreatorMounted = false;
    let crateCreatorMounted = false;
    let shopItemsMounted = false;
    let shopCratesMounted = false;
    let shopCreatorApi: AdminShopCreatorApi | null = null;
    let crateCreatorApi: AdminShopCreatorApi | null = null;

    const hasPerm = (p: AdminPermissionKey) => shell.adminPermissions.includes(p);
    const hasWallAccess = () =>
        hasPerm("wallEdit") || hasPerm("wallPublish") || hasPerm("wallDelete");
    const hasShopDotsAccess = () =>
        hasPerm("shopDots") || hasPerm("shopItems") || hasPerm("records");

    let permHintEl: HTMLElement | null = null;
    let permHintTimer = 0;
    const showPermDeniedHint = () => {
        const host = document.getElementById("sideNav") || document.getElementById("sideNavAdminScroll");
        if (!host) return;
        if (!permHintEl) {
            permHintEl = document.createElement("div");
            permHintEl.className = "sideNavBtnPermHint";
            permHintEl.setAttribute("role", "status");
            host.appendChild(permHintEl);
        } else if (permHintEl.parentElement !== host) {
            host.appendChild(permHintEl);
        }
        permHintEl.textContent = "No permission for this section.";
        permHintEl.classList.add("sideNavBtnPermHint--show");
        if (permHintTimer) window.clearTimeout(permHintTimer);
        permHintTimer = window.setTimeout(() => {
            permHintEl?.classList.remove("sideNavBtnPermHint--show");
        }, 1600);
    };

    const setNavBtnAccess = (btn: HTMLButtonElement, allowed: boolean) => {
        btn.classList.remove("hidden");
        btn.classList.toggle("sideNavBtn--locked", !allowed);
        btn.setAttribute("aria-disabled", allowed ? "false" : "true");
        if (allowed) {
            btn.removeAttribute("title");
        } else {
            btn.title = "No permission for this section";
        }
    };

    const applyNavVisibility = () => {
        setNavBtnAccess(shell.btnRecords, hasPerm("records"));
        setNavBtnAccess(shell.btnPasswordRecovery, hasPerm("passwordRecovery"));
        setNavBtnAccess(shell.btnLevels, hasPerm("speedrunLevels"));
        setNavBtnAccess(shell.btnEditor, hasPerm("speedrunEditor"));
        setNavBtnAccess(shell.btnPenaltyLevels, hasPerm("penaltyLevels"));
        setNavBtnAccess(shell.btnPenaltyEditor, hasPerm("penaltyEditor"));
        setNavBtnAccess(shell.btnPracticeLevels, hasPerm("practiceLevels"));
        setNavBtnAccess(shell.btnPracticeEditor, hasPerm("practiceEditor"));
        setNavBtnAccess(shell.btnWall, hasWallAccess());
        setNavBtnAccess(shell.btnAchievements, hasPerm("achievements"));
        setNavBtnAccess(shell.btnTasks, hasPerm("tasksEditor"));
        setNavBtnAccess(shell.btnBattlePass, hasPerm("battlePassEditor"));
        setNavBtnAccess(shell.btnChallenges, hasPerm("challenges"));
        setNavBtnAccess(shell.btnTournaments, hasPerm("tournamentsEditor"));
        setNavBtnAccess(shell.btnMarathon, hasPerm("marathon"));
        setNavBtnAccess(shell.btnCampaign, hasPerm("campaign"));
        setNavBtnAccess(shell.btnAnnouncements, hasPerm("announcementsSend"));
        setNavBtnAccess(shell.btnWhatsNew, hasPerm("whatsNew"));
        setNavBtnAccess(shell.btnShopCreator, hasPerm("shopCreator"));
        setNavBtnAccess(shell.btnCrateCreator, hasPerm("crateCreator"));
        setNavBtnAccess(shell.btnShopItems, hasPerm("shopItems"));
        setNavBtnAccess(shell.btnShopCrates, hasPerm("crates"));
        setNavBtnAccess(shell.btnShopDots, hasShopDotsAccess());
        setNavBtnAccess(shell.btnSplash, hasPerm("splashEdit"));
        setNavBtnAccess(shell.btnThemeCreator, hasPerm("themeEditor"));
        setNavBtnAccess(shell.btnIconCreator, hasPerm("iconEditor"));
        setNavBtnAccess(shell.btnAdministrators, hasPerm("administrators"));
    };
    applyNavVisibility();

    const guardPerm = (allowed: boolean): boolean => {
        if (allowed) return true;
        showPermDeniedHint();
        return false;
    };

    let recordsOpen = false;
    let passwordRecoveryOpen = false;
    let levelsOpen = false;
    let penaltyLevelsOpen = false;
    let practiceLevelsOpen = false;
    let wallOpen = false;
    let achievementsOpen = false;
    let tasksOpen = false;
    let battlePassOpen = false;
    let challengesOpen = false;
    let tournamentsOpen = false;
    let marathonOpen = false;
    let campaignOpen = false;
    let announcementsOpen = false;
    let whatsNewOpen = false;
    let shopCreatorOpen = false;
    let crateCreatorOpen = false;
    let shopItemsOpen = false;
    let shopCratesOpen = false;
    let shopDotsOpen = false;
    let splashOpen = false;
    let themeCreatorOpen = false;
    let iconCreatorOpen = false;
    let administratorsOpen = false;
    /** Editor opened only via the SpeedRun Editor button (not from the levels list). */
    let standaloneEditorActive = false;
    let standalonePenaltyEditorActive = false;
    let standalonePracticeEditorActive = false;
    let editorFromLevels = false;
    let penaltyEditorFromLevels = false;
    let practiceEditorFromLevels = false;
    let playerStatsOpen = false;
    let recordsResolvedUser: { userId: string; displayName: string } | null = null;
    let activePlayerStatsUserId: string | null = null;

    const syncRecordsManageStatsBtn = () => {
        const show = recordsOpen && !playerStatsOpen && !!recordsResolvedUser;
        shell.recordsManageStatsBtn.classList.toggle("hidden", !show);
    };

    const closePlayerStatsScreen = () => {
        playerStatsOpen = false;
        activePlayerStatsUserId = null;
        shell.playerStatsScreen.classList.add("hidden");
        shell.recordsTableWrap.classList.remove("hidden");
        shell.recordsStatus.classList.remove("hidden");
        syncRecordsManageStatsBtn();
    };

    const editorVisible = () => !shell.panelEditor.classList.contains("hidden");
    const penaltyEditorVisible = () => !shell.panelPenaltyEditor.classList.contains("hidden");
    const practiceEditorVisible = () => !shell.panelPracticeEditor.classList.contains("hidden");

    const syncNavHighlight = () => {
        shell.btnRecords.classList.toggle("green", recordsOpen);
        shell.btnPasswordRecovery.classList.toggle("green", passwordRecoveryOpen);
        shell.btnLevels.classList.toggle("green", levelsOpen);
        shell.btnEditor.classList.toggle("green", standaloneEditorActive && editorVisible());
        shell.btnPenaltyLevels.classList.toggle("green", penaltyLevelsOpen);
        shell.btnPenaltyEditor.classList.toggle("green", standalonePenaltyEditorActive && penaltyEditorVisible());
        shell.btnPracticeLevels.classList.toggle("green", practiceLevelsOpen);
        shell.btnPracticeEditor.classList.toggle("green", standalonePracticeEditorActive && practiceEditorVisible());
        shell.btnWall.classList.toggle("green", wallOpen);
        shell.btnAchievements.classList.toggle("green", achievementsOpen);
        shell.btnTasks.classList.toggle("green", tasksOpen);
        shell.btnBattlePass.classList.toggle("green", battlePassOpen);
        shell.btnChallenges.classList.toggle("green", challengesOpen);
        shell.btnTournaments.classList.toggle("green", tournamentsOpen);
        shell.btnMarathon.classList.toggle("green", marathonOpen);
        shell.btnCampaign.classList.toggle("green", campaignOpen);
        shell.btnAnnouncements.classList.toggle("green", announcementsOpen);
        shell.btnWhatsNew.classList.toggle("green", whatsNewOpen);
        shell.btnShopCreator.classList.toggle("green", shopCreatorOpen);
        shell.btnCrateCreator.classList.toggle("green", crateCreatorOpen);
        shell.btnShopItems.classList.toggle("green", shopItemsOpen);
        shell.btnShopCrates.classList.toggle("green", shopCratesOpen);
        shell.btnShopDots.classList.toggle("green", shopDotsOpen);
        shell.btnSplash.classList.toggle("green", splashOpen);
        shell.btnThemeCreator.classList.toggle("green", themeCreatorOpen);
        shell.btnIconCreator.classList.toggle("green", iconCreatorOpen);
        shell.btnAdministrators.classList.toggle("green", administratorsOpen);
        shell.onVisibilityChange?.();
    };

    const hideAllPanels = () => {
        closePlayerStatsScreen();
        recordsResolvedUser = null;
        syncRecordsManageStatsBtn();
        shell.panelRecords.classList.add("hidden");
        shell.panelPasswordRecovery.classList.add("hidden");
        shell.panelLevels.classList.add("hidden");
        shell.panelEditor.classList.add("hidden");
        shell.panelPenaltyLevels.classList.add("hidden");
        shell.panelPenaltyEditor.classList.add("hidden");
        shell.panelPracticeLevels.classList.add("hidden");
        shell.panelPracticeEditor.classList.add("hidden");
        shell.panelWall.classList.add("hidden");
        shell.panelAchievements.classList.add("hidden");
        shell.panelTasks.classList.add("hidden");
        shell.panelBattlePass.classList.add("hidden");
        shell.panelChallenges.classList.add("hidden");
        shell.panelTournaments.classList.add("hidden");
        shell.panelMarathon.classList.add("hidden");
        shell.panelCampaign.classList.add("hidden");
        shell.panelAnnouncements.classList.add("hidden");
        shell.panelWhatsNew.classList.add("hidden");
        shell.panelShopCreator.classList.add("hidden");
        shell.panelCrateCreator.classList.add("hidden");
        shell.panelShopItems.classList.add("hidden");
        shell.panelShopCrates.classList.add("hidden");
        shell.panelShopDots.classList.add("hidden");
        shell.panelSplash.classList.add("hidden");
        shell.panelThemeCreator.classList.add("hidden");
        shell.panelIconCreator.classList.add("hidden");
        shell.panelAdministrators.classList.add("hidden");
        recordsOpen = false;
        passwordRecoveryOpen = false;
        levelsOpen = false;
        penaltyLevelsOpen = false;
        practiceLevelsOpen = false;
        wallOpen = false;
        achievementsOpen = false;
        tasksOpen = false;
        battlePassOpen = false;
        challengesOpen = false;
        tournamentsOpen = false;
        marathonOpen = false;
        campaignOpen = false;
        announcementsOpen = false;
        whatsNewOpen = false;
        shopCreatorOpen = false;
        crateCreatorOpen = false;
        shopItemsOpen = false;
        shopCratesOpen = false;
        shopDotsOpen = false;
        splashOpen = false;
        themeCreatorOpen = false;
        iconCreatorOpen = false;
        administratorsOpen = false;
        standaloneEditorActive = false;
        standalonePenaltyEditorActive = false;
        standalonePracticeEditorActive = false;
        editorFromLevels = false;
        penaltyEditorFromLevels = false;
        practiceEditorFromLevels = false;
        syncNavHighlight();
    };

    const openShopCreatorPanel = () => {
        if (!guardPerm(hasPerm("shopCreator"))) return;
        hideAllPanels();
        shell.panelShopCreator.classList.remove("hidden");
        shopCreatorOpen = true;
        syncNavHighlight();
        if (!shopCreatorMounted) {
            shopCreatorApi = mountAdminShopCreator(shell.shopCreatorMount, {
              permissions: {
                publish: hasPerm("shopCreator"),
                overwrite: hasPerm("shopOverwrite"),
              },
              onEnterThemeCosmeticPreview: shell.onEnterThemeCosmeticPreview,
            });
            shopCreatorMounted = true;
        }
    };

    const openCrateCreatorPanel = () => {
        if (!guardPerm(hasPerm("crateCreator"))) return;
        hideAllPanels();
        shell.panelCrateCreator.classList.remove("hidden");
        crateCreatorOpen = true;
        syncNavHighlight();
        if (!crateCreatorMounted) {
            crateCreatorApi = mountAdminCrateCreator(shell.crateCreatorMount, {
              permissions: {
                publish: hasPerm("cratesPublish"),
                overwrite: hasPerm("cratesOverwrite"),
              },
              onEnterThemeCosmeticPreview: shell.onEnterThemeCosmeticPreview,
            });
            crateCreatorMounted = true;
        }
    };

    const ensureWallEditor = () => {
        if (!wallApi) {
            wallApi = mountAdminWallEditor(shell.wallMount);
        } else {
            wallApi.refresh();
        }
        return wallApi;
    };

    const ensureEditor = () => {
        if (!editorApi) {
            editorApi = mountAdminSpeedrunEditor(shell.editorMount, {
                onLevelsChanged: shell.onSpeedrunLevelsChanged,
                permissions: {
                    publish: hasPerm("speedrunPublish"),
                    overwrite: hasPerm("speedrunOverwrite"),
                },
            });
        }
        return editorApi;
    };

    const showEditorFromLevel = (_levelIndex: number, def: import("./speedrun-levels").SpeedrunLevelDef) => {
        shell.panelLevels.classList.add("hidden");
        levelsOpen = false;
        standaloneEditorActive = false;
        editorFromLevels = true;
        shell.panelEditor.classList.remove("hidden");
        const api = ensureEditor();
        api.loadLevel(def);
        syncNavHighlight();
    };

    const ensureLevelsList = () => {
        if (!levelsApi) {
            levelsApi = mountAdminSpeedrunLevelsList(shell.levelsMount, {
                onLevelsChanged: shell.onSpeedrunLevelsChanged,
                onEditLayout: (_idx, def) => {
                    showEditorFromLevel(_idx, def);
                },
            });
        } else {
            levelsApi.refresh();
        }
    };

    const ensurePenaltyEditor = () => {
        if (!penaltyEditorApi) {
            penaltyEditorApi = mountAdminPenaltyEditor(shell.penaltyEditorMount, {
                onLevelsChanged: shell.onPenaltyLevelsChanged,
                permissions: {
                    publish: hasPerm("penaltyPublish"),
                    overwrite: hasPerm("penaltyOverwrite"),
                },
            });
        }
        return penaltyEditorApi;
    };

    const showPenaltyEditorFromLevel = (_levelIndex: number, def: import("./penalty-levels").PenaltyLevelDef) => {
        shell.panelPenaltyLevels.classList.add("hidden");
        penaltyLevelsOpen = false;
        standalonePenaltyEditorActive = false;
        penaltyEditorFromLevels = true;
        shell.panelPenaltyEditor.classList.remove("hidden");
        const api = ensurePenaltyEditor();
        api.loadLevel(def);
        syncNavHighlight();
    };

    const ensurePenaltyLevelsList = () => {
        if (!penaltyLevelsApi) {
            penaltyLevelsApi = mountAdminPenaltyLevelsList(shell.penaltyLevelsMount, {
                onEditLayout: (_idx, def) => {
                    showPenaltyEditorFromLevel(_idx, def);
                },
            });
        } else {
            penaltyLevelsApi.refresh();
        }
    };

    const ensurePracticeEditor = () => {
        if (!practiceEditorApi) {
            practiceEditorApi = mountAdminPracticeEditor(shell.practiceEditorMount, {
                onLevelsChanged: shell.onPracticeLevelsChanged,
                permissions: {
                    publish: hasPerm("practicePublish"),
                    overwrite: hasPerm("practiceOverwrite"),
                },
            });
        }
        return practiceEditorApi;
    };

    const showPracticeEditorFromLevel = (_levelIndex: number, def: import("./practice-levels").PracticeLevelDef) => {
        shell.panelPracticeLevels.classList.add("hidden");
        practiceLevelsOpen = false;
        standalonePracticeEditorActive = false;
        practiceEditorFromLevels = true;
        shell.panelPracticeEditor.classList.remove("hidden");
        const api = ensurePracticeEditor();
        api.loadLevel(def);
        syncNavHighlight();
    };

    const ensurePracticeLevelsList = () => {
        if (!practiceLevelsApi) {
            practiceLevelsApi = mountAdminPracticeLevelsList(shell.practiceLevelsMount, {
                onLevelsChanged: shell.onPracticeLevelsChanged,
                onEditLayout: (_idx, def) => {
                    showPracticeEditorFromLevel(_idx, def);
                },
                onShowInCategoriesChanged: (show) => {
                    setPracticeHubVisible(show);
                },
            });
        } else {
            practiceLevelsApi.refresh();
        }
    };

    function buildPermissionEditor(
        container: HTMLElement,
        user: { id: string; displayName: string | null; email: string | null; permissions: AdminPermissionKey[] },
        onSaved: () => void,
    ) {
        if (shell.currentUserId && user.id === shell.currentUserId) {
            const note = document.createElement("p");
            note.className = "accessHint";
            note.textContent = "You cannot change your own permissions. Edit other users instead.";
            container.appendChild(note);
            const list = document.createElement("p");
            list.className = "accessHint";
            list.textContent = user.permissions.length
                ? user.permissions.map((key) => ADMIN_PERMISSION_LABELS[key]).join(", ")
                : "NONE";
            container.appendChild(list);
            return;
        }

        const grid = document.createElement("div");
        grid.className = "adminPermGrid";
        const checks: Partial<Record<AdminPermissionKey, HTMLInputElement>> = {};
        for (const key of ADMIN_PERMISSION_ORDER) {
            const label = document.createElement("label");
            label.className = "adminPermCheck";
            const cb = document.createElement("input");
            cb.type = "checkbox";
            cb.checked = user.permissions.includes(key);
            checks[key] = cb;
            label.appendChild(cb);
            label.appendChild(document.createTextNode(ADMIN_PERMISSION_LABELS[key]));
            grid.appendChild(label);
        }
        container.appendChild(grid);
        const save = document.createElement("button");
        save.type = "button";
        save.className = "btn green";
        save.textContent = "Save permissions";
        save.onclick = () => {
            const next: AdminPermissionKey[] = [];
            for (const key of ADMIN_PERMISSION_ORDER) {
                if (checks[key]?.checked) next.push(key);
            }
            void (async () => {
                shell.administratorsStatus.textContent = "Saving…";
                try {
                    await patchAdminUserPermissions(user.id, next);
                    shell.administratorsStatus.textContent = "Permissions saved.";
                    onSaved();
                } catch {
                    shell.administratorsStatus.textContent = "Save failed (forbidden or network).";
                }
            })();
        };
        container.appendChild(save);
    }

    async function loadAdministrators(opts?: { search?: string; listAll?: boolean }) {
        shell.administratorsTableWrap.innerHTML = "";
        shell.administratorsStatus.textContent = "Loading…";
        try {
            const search = opts?.search?.trim() ?? "";
            const items = await fetchAdminGrantUsers(opts?.listAll ? undefined : search);
            if (!items.length) {
                shell.administratorsStatus.textContent = opts?.listAll
                    ? "No users with admin permissions yet."
                    : "No users match.";
                return;
            }
            shell.administratorsStatus.textContent = `${items.length} user(s). Default is NONE until you grant permissions.`;
            const tbl = document.createElement("table");
            tbl.className = "lbTable adminRecordsTable";
            const thead = document.createElement("thead");
            const hr = document.createElement("tr");
            ["Username", "Email", "Permissions"].forEach((h) => {
                const th = document.createElement("th");
                th.textContent = h;
                hr.appendChild(th);
            });
            thead.appendChild(hr);
            tbl.appendChild(thead);
            const tb = document.createElement("tbody");
            for (const u of items) {
                const tr = document.createElement("tr");
                const tdName = document.createElement("td");
                tdName.textContent = u.displayName || "—";
                const tdEmail = document.createElement("td");
                tdEmail.textContent = u.email || "—";
                const tdPerm = document.createElement("td");
                buildPermissionEditor(tdPerm, u, () => void loadAdministrators(opts));
                tr.appendChild(tdName);
                tr.appendChild(tdEmail);
                tr.appendChild(tdPerm);
                tb.appendChild(tr);
            }
            tbl.appendChild(tb);
            shell.administratorsTableWrap.appendChild(tbl);
        } catch {
            shell.administratorsStatus.textContent = "Could not load (admin session / database).";
        }
    }

    shell.btnAdministrators.onclick = () => {
        if (!guardPerm(hasPerm("administrators"))) return;
        if (administratorsOpen) {
            hideAllPanels();
            return;
        }
        shell.panelRecords.classList.add("hidden");
        shell.panelPasswordRecovery.classList.add("hidden");
        shell.panelLevels.classList.add("hidden");
        shell.panelEditor.classList.add("hidden");
        shell.panelWall.classList.add("hidden");
        shell.panelAchievements.classList.add("hidden");
        recordsOpen = false;
        passwordRecoveryOpen = false;
        levelsOpen = false;
        wallOpen = false;
        achievementsOpen = false;
        standaloneEditorActive = false;
        editorFromLevels = false;
        shell.panelAdministrators.classList.remove("hidden");
        administratorsOpen = true;
        syncNavHighlight();
        void loadAdministrators({ listAll: true });
    };

    shell.administratorsSearchBtn.onclick = () => {
        const q = shell.administratorsSearch.value.trim();
        if (q.length < 2) {
            shell.administratorsStatus.textContent = "Enter at least 2 characters to search.";
            return;
        }
        void loadAdministrators({ search: q });
    };

    shell.administratorsListBtn.onclick = () => void loadAdministrators({ listAll: true });

    shell.btnRecords.onclick = () => {
        if (!guardPerm(hasPerm("records"))) return;
        if (recordsOpen) {
            hideAllPanels();
            return;
        }
        shell.panelLevels.classList.add("hidden");
        shell.panelPasswordRecovery.classList.add("hidden");
        shell.panelEditor.classList.add("hidden");
        shell.panelWall.classList.add("hidden");
        shell.panelAchievements.classList.add("hidden");
        shell.panelAdministrators.classList.add("hidden");
        levelsOpen = false;
        passwordRecoveryOpen = false;
        wallOpen = false;
        achievementsOpen = false;
        administratorsOpen = false;
        standaloneEditorActive = false;
        editorFromLevels = false;
        shell.panelRecords.classList.remove("hidden");
        recordsOpen = true;
        syncNavHighlight();
        void loadRecords();
        void resolveRecordsSearchUser();
        void loadActiveTimeoutUsers();
    };

    shell.btnPasswordRecovery.onclick = () => {
        if (!guardPerm(hasPerm("passwordRecovery"))) return;
        if (passwordRecoveryOpen) {
            hideAllPanels();
            return;
        }
        shell.panelRecords.classList.add("hidden");
        shell.panelLevels.classList.add("hidden");
        shell.panelEditor.classList.add("hidden");
        shell.panelWall.classList.add("hidden");
        shell.panelAchievements.classList.add("hidden");
        shell.panelAdministrators.classList.add("hidden");
        recordsOpen = false;
        levelsOpen = false;
        wallOpen = false;
        achievementsOpen = false;
        administratorsOpen = false;
        standaloneEditorActive = false;
        editorFromLevels = false;
        shell.panelPasswordRecovery.classList.remove("hidden");
        passwordRecoveryOpen = true;
        syncNavHighlight();
        shell.passwordRecoveryTableWrap.innerHTML = "";
        shell.passwordRecoveryStatus.textContent = "";
        shell.passwordRecoveryResult.classList.add("hidden");
    };

    async function loadPasswordRecoveryUsers(opts?: { keepResult?: boolean }) {
        shell.passwordRecoveryTableWrap.innerHTML = "";
        if (!opts?.keepResult) {
            shell.passwordRecoveryResult.classList.add("hidden");
        }
        const q = shell.passwordRecoverySearch.value.trim();
        if (q.length < 2) {
            shell.passwordRecoveryStatus.textContent = "Enter at least 2 characters to search.";
            return;
        }
        shell.passwordRecoveryStatus.textContent = "Searching…";
        try {
            const users = await fetchAdminPasswordRecoveryUsers(q);
            if (!users.length) {
                shell.passwordRecoveryStatus.textContent = "No users match.";
                return;
            }
            shell.passwordRecoveryStatus.textContent = `${users.length} user(s) found.`;
            const tbl = document.createElement("table");
            tbl.className = "lbTable adminRecordsTable";
            const thead = document.createElement("thead");
            const hr = document.createElement("tr");
            ["Username", "Email", "Pending reset", "Action"].forEach((h) => {
                const th = document.createElement("th");
                th.textContent = h;
                hr.appendChild(th);
            });
            thead.appendChild(hr);
            tbl.appendChild(thead);
            const tb = document.createElement("tbody");
            for (const u of users) {
                const tr = document.createElement("tr");
                const btn = document.createElement("button");
                btn.type = "button";
                btn.className = "btn green";
                btn.textContent = "Create Temporary Password";
                btn.onclick = () => {
                    if (
                        !confirm(
                            `Create a temporary password for ${u.displayName || "?"}? Their current password will stop working immediately.`,
                        )
                    ) {
                        return;
                    }
                    void (async () => {
                        shell.passwordRecoveryStatus.textContent = "Creating temporary password…";
                        try {
                            const result = await createAdminTempPassword(u.id);
                            showTempPasswordResult(result);
                            void loadPasswordRecoveryUsers({ keepResult: true });
                        } catch {
                            shell.passwordRecoveryStatus.textContent =
                                "Could not create temporary password (forbidden, no email, or network).";
                        }
                    })();
                };
                const cells = [
                    u.displayName || "—",
                    u.email || "—",
                    u.pendingPasswordReset ? "Yes" : "No",
                ];
                for (const txt of cells) {
                    const td = document.createElement("td");
                    td.textContent = txt;
                    tr.appendChild(td);
                }
                const tdAct = document.createElement("td");
                tdAct.appendChild(btn);
                tr.appendChild(tdAct);
                tb.appendChild(tr);
            }
            tbl.appendChild(tb);
            shell.passwordRecoveryTableWrap.appendChild(tbl);
        } catch {
            shell.passwordRecoveryStatus.textContent = "Search failed (admin session / database).";
        }
    }

    function showTempPasswordResult(result: {
        displayName: string | null;
        email: string | null;
        tempPassword: string;
    }) {
        shell.tempPasswordUser.textContent = result.displayName || "?";
        shell.tempPasswordEmail.textContent = result.email || "—";
        shell.tempPasswordValue.textContent = result.tempPassword;
        shell.passwordRecoveryResult.classList.remove("hidden");
        shell.passwordRecoveryResult.scrollIntoView({ behavior: "smooth", block: "nearest" });
        shell.passwordRecoveryStatus.textContent =
            "Temporary password created. Copy it below and share it with the user.";
    }

    shell.tempPasswordCopyBtn.onclick = () => {
        const value = shell.tempPasswordValue.textContent?.trim() || "";
        if (!value) return;
        void navigator.clipboard.writeText(value).then(
            () => {
                shell.passwordRecoveryStatus.textContent = "Temporary password copied to clipboard.";
            },
            () => {
                shell.passwordRecoveryStatus.textContent = "Select the password above and copy manually.";
            },
        );
    };

    shell.passwordRecoverySearchBtn.onclick = () => void loadPasswordRecoveryUsers();
    shell.passwordRecoverySearch.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") void loadPasswordRecoveryUsers();
    });

    shell.btnLevels.onclick = () => {
        if (!guardPerm(hasPerm("speedrunLevels"))) return;
        if (levelsOpen && !shell.panelEditor.classList.contains("hidden")) {
            shell.panelEditor.classList.add("hidden");
            editorFromLevels = false;
            standaloneEditorActive = false;
            levelsApi?.refresh();
            syncNavHighlight();
            return;
        }
        if (levelsOpen) {
            hideAllPanels();
            return;
        }
        shell.panelRecords.classList.add("hidden");
        shell.panelPasswordRecovery.classList.add("hidden");
        shell.panelEditor.classList.add("hidden");
        shell.panelWall.classList.add("hidden");
        shell.panelAchievements.classList.add("hidden");
        shell.panelAdministrators.classList.add("hidden");
        recordsOpen = false;
        passwordRecoveryOpen = false;
        wallOpen = false;
        achievementsOpen = false;
        administratorsOpen = false;
        standaloneEditorActive = false;
        editorFromLevels = false;
        shell.panelLevels.classList.remove("hidden");
        levelsOpen = true;
        syncNavHighlight();
        ensureLevelsList();
    };

    shell.btnEditor.onclick = () => {
        if (!guardPerm(hasPerm("speedrunEditor"))) return;
        if (standaloneEditorActive && editorVisible()) {
            hideAllPanels();
            return;
        }
        shell.panelRecords.classList.add("hidden");
        shell.panelPasswordRecovery.classList.add("hidden");
        shell.panelLevels.classList.add("hidden");
        shell.panelWall.classList.add("hidden");
        shell.panelAchievements.classList.add("hidden");
        shell.panelAdministrators.classList.add("hidden");
        recordsOpen = false;
        passwordRecoveryOpen = false;
        levelsOpen = false;
        wallOpen = false;
        achievementsOpen = false;
        administratorsOpen = false;
        standaloneEditorActive = true;
        editorFromLevels = false;
        shell.panelEditor.classList.remove("hidden");
        ensureEditor().refitCanvas();
        syncNavHighlight();
    };

    shell.btnPenaltyLevels.onclick = () => {
        if (!guardPerm(hasPerm("penaltyLevels"))) return;
        if (penaltyLevelsOpen && !shell.panelPenaltyEditor.classList.contains("hidden")) {
            shell.panelPenaltyEditor.classList.add("hidden");
            penaltyEditorFromLevels = false;
            standalonePenaltyEditorActive = false;
            penaltyLevelsApi?.refresh();
            syncNavHighlight();
            return;
        }
        if (penaltyLevelsOpen) {
            hideAllPanels();
            return;
        }
        shell.panelRecords.classList.add("hidden");
        shell.panelPasswordRecovery.classList.add("hidden");
        shell.panelLevels.classList.add("hidden");
        shell.panelEditor.classList.add("hidden");
        shell.panelPenaltyEditor.classList.add("hidden");
        shell.panelWall.classList.add("hidden");
        shell.panelAchievements.classList.add("hidden");
        shell.panelAdministrators.classList.add("hidden");
        recordsOpen = false;
        passwordRecoveryOpen = false;
        levelsOpen = false;
        wallOpen = false;
        achievementsOpen = false;
        administratorsOpen = false;
        standaloneEditorActive = false;
        standalonePenaltyEditorActive = false;
        editorFromLevels = false;
        penaltyEditorFromLevels = false;
        shell.panelPenaltyLevels.classList.remove("hidden");
        penaltyLevelsOpen = true;
        syncNavHighlight();
        ensurePenaltyLevelsList();
    };

    shell.btnPenaltyEditor.onclick = () => {
        if (!guardPerm(hasPerm("penaltyEditor"))) return;
        if (standalonePenaltyEditorActive && penaltyEditorVisible()) {
            hideAllPanels();
            return;
        }
        shell.panelRecords.classList.add("hidden");
        shell.panelPasswordRecovery.classList.add("hidden");
        shell.panelLevels.classList.add("hidden");
        shell.panelEditor.classList.add("hidden");
        shell.panelPenaltyLevels.classList.add("hidden");
        shell.panelWall.classList.add("hidden");
        shell.panelAchievements.classList.add("hidden");
        shell.panelAdministrators.classList.add("hidden");
        recordsOpen = false;
        passwordRecoveryOpen = false;
        levelsOpen = false;
        penaltyLevelsOpen = false;
        wallOpen = false;
        achievementsOpen = false;
        administratorsOpen = false;
        standaloneEditorActive = false;
        standalonePenaltyEditorActive = true;
        editorFromLevels = false;
        penaltyEditorFromLevels = false;
        shell.panelPenaltyEditor.classList.remove("hidden");
        ensurePenaltyEditor().refitCanvas();
        syncNavHighlight();
    };

    shell.btnPracticeLevels.onclick = () => {
        if (!guardPerm(hasPerm("practiceLevels"))) return;
        if (practiceLevelsOpen && !shell.panelPracticeEditor.classList.contains("hidden")) {
            shell.panelPracticeEditor.classList.add("hidden");
            practiceEditorFromLevels = false;
            standalonePracticeEditorActive = false;
            practiceLevelsApi?.refresh();
            syncNavHighlight();
            return;
        }
        if (practiceLevelsOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelPracticeLevels.classList.remove("hidden");
        practiceLevelsOpen = true;
        syncNavHighlight();
        ensurePracticeLevelsList();
    };

    shell.btnPracticeEditor.onclick = () => {
        if (!guardPerm(hasPerm("practiceEditor"))) return;
        if (standalonePracticeEditorActive && practiceEditorVisible()) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        standalonePracticeEditorActive = true;
        practiceEditorFromLevels = false;
        shell.panelPracticeEditor.classList.remove("hidden");
        ensurePracticeEditor().refitCanvas();
        syncNavHighlight();
    };

    shell.btnWall.onclick = () => {
        if (!guardPerm(hasWallAccess())) return;
        if (wallOpen) {
            hideAllPanels();
            return;
        }
        shell.panelRecords.classList.add("hidden");
        shell.panelPasswordRecovery.classList.add("hidden");
        shell.panelLevels.classList.add("hidden");
        shell.panelEditor.classList.add("hidden");
        shell.panelAchievements.classList.add("hidden");
        shell.panelAdministrators.classList.add("hidden");
        recordsOpen = false;
        passwordRecoveryOpen = false;
        levelsOpen = false;
        achievementsOpen = false;
        administratorsOpen = false;
        standaloneEditorActive = false;
        editorFromLevels = false;
        shell.panelWall.classList.remove("hidden");
        wallOpen = true;
        syncNavHighlight();
        ensureWallEditor();
    };

    shell.btnAchievements.onclick = () => {
        if (!guardPerm(hasPerm("achievements"))) return;
        if (achievementsOpen) {
            hideAllPanels();
            return;
        }
        shell.panelRecords.classList.add("hidden");
        shell.panelPasswordRecovery.classList.add("hidden");
        shell.panelLevels.classList.add("hidden");
        shell.panelEditor.classList.add("hidden");
        shell.panelWall.classList.add("hidden");
        shell.panelAdministrators.classList.add("hidden");
        recordsOpen = false;
        passwordRecoveryOpen = false;
        levelsOpen = false;
        wallOpen = false;
        administratorsOpen = false;
        standaloneEditorActive = false;
        editorFromLevels = false;
        shell.panelAchievements.classList.remove("hidden");
        achievementsOpen = true;
        syncNavHighlight();
        if (!achievementsApi) achievementsApi = mountAdminAchievements(shell.achievementsMount);
        else achievementsApi.refresh();
    };

    shell.btnTasks.onclick = () => {
        if (!guardPerm(hasPerm("tasksEditor"))) return;
        if (tasksOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelTasks.classList.remove("hidden");
        tasksOpen = true;
        syncNavHighlight();
        if (!tasksApi) tasksApi = mountAdminTasks(shell.tasksMount);
        else tasksApi.refresh();
    };

    shell.btnBattlePass.onclick = () => {
        if (!guardPerm(hasPerm("battlePassEditor"))) return;
        if (battlePassOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelBattlePass.classList.remove("hidden");
        battlePassOpen = true;
        syncNavHighlight();
        if (!battlePassApi) battlePassApi = mountAdminBattlePass(shell.battlePassMount);
        else battlePassApi.refresh();
    };

    shell.btnChallenges.onclick = () => {
        if (!guardPerm(hasPerm("challenges"))) return;
        if (challengesOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelChallenges.classList.remove("hidden");
        challengesOpen = true;
        syncNavHighlight();
        if (!challengesApi) challengesApi = mountAdminChallenges(shell.challengesMount);
        else challengesApi.refresh();
    };

    shell.btnTournaments.onclick = () => {
        if (!guardPerm(hasPerm("tournamentsEditor"))) return;
        if (tournamentsOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelTournaments.classList.remove("hidden");
        tournamentsOpen = true;
        syncNavHighlight();
        mountAdminTournaments(shell.tournamentsMount);
        tournamentsMounted = true;
    };

    shell.btnMarathon.onclick = () => {
        if (!guardPerm(hasPerm("marathon"))) return;
        if (marathonOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelMarathon.classList.remove("hidden");
        marathonOpen = true;
        syncNavHighlight();
        if (!marathonApi) marathonApi = mountAdminMarathon(shell.marathonMount);
        else marathonApi.refresh();
    };

    shell.btnCampaign.onclick = () => {
        if (!guardPerm(hasPerm("campaign"))) return;
        if (campaignOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelCampaign.classList.remove("hidden");
        campaignOpen = true;
        syncNavHighlight();
        if (!campaignApi) campaignApi = mountAdminCampaign(shell.campaignMount);
        else campaignApi.refresh();
    };

    shell.btnAnnouncements.onclick = () => {
        if (!guardPerm(hasPerm("announcementsSend"))) return;
        if (announcementsOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelAnnouncements.classList.remove("hidden");
        announcementsOpen = true;
        syncNavHighlight();
        if (!announcementsApi) announcementsApi = mountAdminAnnouncements(shell.announcementsMount);
        else announcementsApi.refresh();
    };

    shell.btnWhatsNew.onclick = () => {
        if (!guardPerm(hasPerm("whatsNew"))) return;
        if (whatsNewOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelWhatsNew.classList.remove("hidden");
        whatsNewOpen = true;
        syncNavHighlight();
        if (!whatsNewApi) whatsNewApi = mountAdminWhatsNew(shell.whatsNewMount);
        else whatsNewApi.refresh();
    };

    shell.btnShopCreator.onclick = () => {
        if (!guardPerm(hasPerm("shopCreator"))) return;
        if (shopCreatorOpen) {
            hideAllPanels();
            return;
        }
        openShopCreatorPanel();
    };

    shell.btnCrateCreator.onclick = () => {
        if (!guardPerm(hasPerm("crateCreator"))) return;
        if (crateCreatorOpen) {
            hideAllPanels();
            return;
        }
        openCrateCreatorPanel();
    };

    shell.btnShopItems.onclick = () => {
        if (!guardPerm(hasPerm("shopItems"))) return;
        if (shopItemsOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelShopItems.classList.remove("hidden");
        shopItemsOpen = true;
        syncNavHighlight();
        if (!shopItemsMounted) {
            mountAdminShopItems(shell.shopItemsMount, {
              permissions: {
                overwrite: hasPerm("shopOverwrite"),
                delete: hasPerm("shopDelete"),
              },
              onEditItem: (item) => {
                if (!hasPerm("shopCreator")) return;
                openShopCreatorPanel();
                shopCreatorApi?.loadPublishedItem(item);
              },
            });
            shopItemsMounted = true;
        }
    };

    shell.btnShopCrates.onclick = () => {
        if (!guardPerm(hasPerm("crates"))) return;
        if (shopCratesOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelShopCrates.classList.remove("hidden");
        shopCratesOpen = true;
        syncNavHighlight();
        if (!shopCratesMounted) {
            mountAdminShopCrates(shell.shopCratesMount, {
              permissions: {
                overwrite: hasPerm("cratesOverwrite"),
                delete: hasPerm("crates"),
              },
              onEditCrateItem: (item) => {
                if (!hasPerm("crateCreator")) return;
                openCrateCreatorPanel();
                crateCreatorApi?.loadPublishedItem?.({
                  id: item.id,
                  name: item.name,
                  imageData: item.imageData,
                  outlineMaskData: item.outlineMaskData ?? null,
                  animationFrames: item.animationFrames ?? null,
                  category: item.category ?? "cursor",
                  trailMode: item.trailMode ?? null,
                  trailColors: item.trailColors ?? null,
                  allowCustomColor: item.allowCustomColor,
                  allowRepurchase: item.allowRepurchase,
                  matchCursorColor: item.matchCursorColor,
                  cursorColorShade: item.cursorColorShade,
                  matchCursorMaskData: item.matchCursorMaskData ?? null,
                  matchCursorMaskFrames: item.matchCursorMaskFrames ?? null,
                  unlockType: item.unlockType,
                  priceDots: item.priceDots,
                  unlockAchievementId: item.unlockAchievementId,
                  unlockChallengeId: item.unlockChallengeId,
                  unlockScoreMode: item.unlockScoreMode,
                  unlockScoreValue: item.unlockScoreValue,
                });
              },
            });
            shopCratesMounted = true;
        }
    };

    shell.btnShopDots.onclick = () => {
        if (!guardPerm(hasShopDotsAccess())) return;
        if (shopDotsOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelShopDots.classList.remove("hidden");
        shopDotsOpen = true;
        syncNavHighlight();
    };
    shell.shopDotsSearchBtn.onclick = () => void runShopDotsSearch();
    shell.shopDotsSearch.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") void runShopDotsSearch();
    });

    shell.btnSplash.onclick = () => {
        if (!guardPerm(hasPerm("splashEdit"))) return;
        if (splashOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelSplash.classList.remove("hidden");
        splashOpen = true;
        syncNavHighlight();
        if (!splashApi) splashApi = mountAdminSplashEditor(shell.splashMount);
        else splashApi.refresh();
    };

    shell.btnThemeCreator.onclick = () => {
        if (!guardPerm(hasPerm("themeEditor"))) return;
        if (themeCreatorOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelThemeCreator.classList.remove("hidden");
        themeCreatorOpen = true;
        syncNavHighlight();
        if (!themeCreatorApi) {
            themeCreatorApi = mountAdminThemeCreator(shell.themeCreatorMount, {
                enterPreview: (api) => {
                    if (shell.onEnterThemePreview) shell.onEnterThemePreview(api);
                },
            });
        } else {
            themeCreatorApi.refresh();
        }
    };

    shell.btnIconCreator.onclick = () => {
        if (!guardPerm(hasPerm("iconEditor"))) return;
        if (iconCreatorOpen) {
            hideAllPanels();
            return;
        }
        hideAllPanels();
        shell.panelIconCreator.classList.remove("hidden");
        iconCreatorOpen = true;
        syncNavHighlight();
        if (!iconCreatorApi) {
            iconCreatorApi = mountAdminIconCreator(shell.iconCreatorMount);
        } else {
            iconCreatorApi.refresh();
        }
    };

    const syncSpeedrunFilterRow = () => {
        const on = shell.recordsMode.value === "speedrun";
        shell.recordsSpeedrunRow.classList.toggle("hidden", !on);
    };

    const openPlayerStatsScreen = (userId: string, label: string) => {
        playerStatsOpen = true;
        activePlayerStatsUserId = userId;
        shell.recordsTableWrap.classList.add("hidden");
        shell.recordsStatus.classList.add("hidden");
        shell.playerStatsScreen.classList.remove("hidden");
        shell.playerStatsTitle.textContent = `Player stats — ${label}`;
        syncRecordsManageStatsBtn();
        void loadPlayerStatsDetail(userId);
    };

    let recordsSearchTimer: ReturnType<typeof setTimeout> | null = null;
    let recordsLookupTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleRecordsReload = () => {
        if (!recordsOpen || playerStatsOpen) return;
        void loadRecords();
    };

    const scheduleRecordsUserLookup = () => {
        if (!recordsOpen || playerStatsOpen) return;
        if (recordsLookupTimer) clearTimeout(recordsLookupTimer);
        recordsLookupTimer = setTimeout(() => void resolveRecordsSearchUser(), 350);
    };

    async function resolveRecordsSearchUser() {
        const q = shell.recordsSearch.value.trim();
        recordsResolvedUser = null;
        syncRecordsManageStatsBtn();
        if (q.length < 2) return;
        try {
            const result = await lookupAdminPlayerStats(q);
            if (result.kind === "found") {
                recordsResolvedUser = {
                    userId: result.detail.userId,
                    displayName: result.detail.displayName || result.detail.email || result.detail.userId,
                };
            }
        } catch {
            recordsResolvedUser = null;
        }
        syncRecordsManageStatsBtn();
    }

    shell.recordsMode.onchange = () => {
        syncSpeedrunFilterRow();
        scheduleRecordsReload();
    };
    shell.recordsSpeedrunLevel.onchange = () => scheduleRecordsReload();
    shell.recordsSearch.addEventListener("input", () => {
        if (recordsSearchTimer) clearTimeout(recordsSearchTimer);
        recordsSearchTimer = setTimeout(() => scheduleRecordsReload(), 350);
        scheduleRecordsUserLookup();
    });

    shell.recordsManageStatsBtn.textContent = "Delete stats";
    shell.recordsManageStatsBtn.onclick = () => {
        if (!recordsResolvedUser) return;
        openPlayerStatsScreen(recordsResolvedUser.userId, recordsResolvedUser.displayName);
    };
    shell.playerStatsBackBtn.onclick = () => closePlayerStatsScreen();

    type PlayerStatChangeTarget = { userId: string; kind: "playtime" | "deaths" };
    let playerStatChangeTarget: PlayerStatChangeTarget | null = null;

    const closePlayerStatChangeModal = () => {
        playerStatChangeTarget = null;
        shell.playerStatsChangeModal.classList.add("hidden");
        shell.playerStatsChangeError.textContent = "";
    };

    const openPlayerStatChangeModal = (userId: string, kind: "playtime" | "deaths", current: number) => {
        playerStatChangeTarget = { userId, kind };
        shell.playerStatsChangeLabel.textContent = kind === "playtime" ? "Playtime" : "Deaths";
        shell.playerStatsChangeCurrent.textContent =
            kind === "playtime" ? `Current: ${formatPlaytimeDhms(current)}` : `Current: ${current}`;
        shell.playerStatsChangeInput.value = kind === "playtime" ? formatPlaytimeDhms(current) : String(current);
        shell.playerStatsChangeHint.textContent =
            kind === "playtime"
                ? "Use d, h, m, s (e.g. 0d 1h 0m 0s)."
                : "Enter a whole number of deaths.";
        shell.playerStatsChangeError.textContent = "";
        shell.playerStatsChangeModal.classList.remove("hidden");
        shell.playerStatsChangeInput.focus();
        shell.playerStatsChangeInput.select();
    };

    async function applyPlayerStatChangeModal() {
        const ctx = playerStatChangeTarget;
        if (!ctx) return;
        const raw = shell.playerStatsChangeInput.value.trim();
        let next: number | null = null;
        if (ctx.kind === "playtime") {
            next = parsePlaytimeDhms(raw);
            if (next == null) {
                shell.playerStatsChangeError.textContent = "Invalid playtime. Use e.g. 0d 1h 0m 0s.";
                return;
            }
        } else {
            const n = Math.floor(Number(raw));
            if (!Number.isFinite(n) || n < 0) {
                shell.playerStatsChangeError.textContent = "Enter a valid non-negative number.";
                return;
            }
            next = n;
        }
        shell.playerStatsChangeError.textContent = "";
        try {
            await patchAdminPlayerStats(
                ctx.userId,
                ctx.kind === "playtime" ? { totalPlayTimeSec: next } : { totalDeaths: next },
            );
            await notifyRecordsChanged(ctx.userId);
            closePlayerStatChangeModal();
            await loadPlayerStatsDetail(ctx.userId);
            if (recordsOpen && !playerStatsOpen) void loadRecords();
        } catch {
            shell.playerStatsChangeError.textContent = "Could not save (forbidden or network).";
        }
    }

    shell.playerStatsChangeCancelBtn.onclick = closePlayerStatChangeModal;
    shell.playerStatsChangeApplyBtn.onclick = () => void applyPlayerStatChangeModal();
    shell.playerStatsChangeModal.addEventListener("click", (ev) => {
        if (ev.target === shell.playerStatsChangeModal) closePlayerStatChangeModal();
    });
    shell.playerStatsChangeInput.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") void applyPlayerStatChangeModal();
        if (ev.key === "Escape") closePlayerStatChangeModal();
    });

    function addStatActionBtn(parent: HTMLElement, text: string, onClick: () => void) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "btn adminRecordsBtn";
        btn.textContent = text;
        btn.onclick = onClick;
        parent.appendChild(btn);
    }

    function renderPlayerStatsCore(detail: AdminPlayerStatsDetailApi) {
        shell.playerStatsCore.replaceChildren();
        const rows: Array<{ label: string; value: string; kind: "playtime" | "deaths" }> = [
            { label: "Playtime", value: formatPlaytimeDhms(detail.totalPlayTimeSec), kind: "playtime" },
            { label: "Deaths", value: String(detail.totalDeaths), kind: "deaths" },
        ];
        for (const row of rows) {
            const el = document.createElement("div");
            el.className = "adminPlayerStatsCoreRow";
            const lbl = document.createElement("span");
            lbl.className = "adminPlayerStatsCoreLabel";
            lbl.textContent = row.label;
            const val = document.createElement("span");
            val.className = "adminPlayerStatsCoreValue";
            val.textContent = row.value;
            const actions = document.createElement("span");
            actions.className = "adminRecordsActions";
            addStatActionBtn(actions, "Change", () =>
                openPlayerStatChangeModal(
                    detail.userId,
                    row.kind,
                    row.kind === "playtime" ? detail.totalPlayTimeSec : detail.totalDeaths,
                ),
            );
            addStatActionBtn(actions, "Delete", () => {
                const msg =
                    row.kind === "playtime"
                        ? `Reset playtime for ${detail.displayName || "?"} to 0?`
                        : `Reset deaths for ${detail.displayName || "?"} to 0?`;
                if (!confirm(msg)) return;
                void (async () => {
                    try {
                        await deleteAdminPlayerStat(detail.userId, row.kind);
                        await notifyRecordsChanged(detail.userId);
                        await loadPlayerStatsDetail(detail.userId);
                    } catch {
                        shell.playerStatsStatus.textContent = "Delete failed (forbidden or network).";
                    }
                })();
            });
            el.appendChild(lbl);
            el.appendChild(val);
            el.appendChild(actions);
            shell.playerStatsCore.appendChild(el);
        }
        const resetRow = document.createElement("div");
        resetRow.className = "adminPlayerStatsCoreRow";
        const resetLbl = document.createElement("span");
        resetLbl.className = "adminPlayerStatsCoreLabel";
        resetLbl.textContent = "All stats";
        const resetVal = document.createElement("span");
        resetVal.className = "adminPlayerStatsCoreValue";
        resetVal.textContent = "Reset playtime, deaths, synced records, and achievements";
        const resetActions = document.createElement("span");
        resetActions.className = "adminRecordsActions";
        addStatActionBtn(resetActions, "Delete all", () => {
            if (
                !confirm(
                    `Delete ALL stats for ${detail.displayName || "?"}? Resets playtime, deaths, synced records, and achievements (not leaderboard rows).`,
                )
            ) {
                return;
            }
            void (async () => {
                try {
                    await deleteAdminUserStats(detail.userId);
                    await notifyRecordsChanged(detail.userId);
                    await loadPlayerStatsDetail(detail.userId);
                } catch {
                    shell.playerStatsStatus.textContent = "Delete all failed (forbidden or network).";
                }
            })();
        });
        resetRow.appendChild(resetLbl);
        resetRow.appendChild(resetVal);
        resetRow.appendChild(resetActions);
        shell.playerStatsCore.appendChild(resetRow);
    }

    let shopDotsActiveUserId: string | null = null;

    function renderShopDotsBalance(detail: AdminPlayerStatsDetailApi) {
        shell.shopDotsBalance.replaceChildren();
        const balanceRow = document.createElement("div");
        balanceRow.className = "adminPlayerStatsCoreRow";
        const lbl = document.createElement("span");
        lbl.className = "adminPlayerStatsCoreLabel";
        lbl.textContent = "Balance";
        const val = document.createElement("span");
        val.className = "adminPlayerStatsCoreValue";
        val.textContent = `${Math.max(0, Math.floor(Number(detail.dotsBalance) || 0)).toLocaleString()} Dots`;
        balanceRow.appendChild(lbl);
        balanceRow.appendChild(val);
        shell.shopDotsBalance.appendChild(balanceRow);

        const controls = document.createElement("div");
        controls.className = "adminPlayerStatsDotsControls modeBox";
        const amount = document.createElement("input");
        amount.type = "number";
        amount.min = "0";
        amount.step = "1";
        amount.className = "formInput";
        amount.placeholder = "Amount";
        amount.setAttribute("aria-label", "Dots amount");
        const run = async (mode: "set" | "add" | "subtract", label: string) => {
            const n = Math.floor(Number(amount.value));
            if (!Number.isFinite(n) || n < 0) {
                shell.shopDotsStatus.textContent = "Enter a valid non-negative Dots amount.";
                return;
            }
            if (!confirm(`${label} ${n.toLocaleString()} Dots for ${detail.displayName || "this player"}?`)) return;
            try {
                await patchAdminPlayerDots(detail.userId, mode, n);
                await loadShopDotsDetail(detail.userId);
                shell.shopDotsStatus.textContent = `Dots updated (${label.toLowerCase()} ${n.toLocaleString()}).`;
            } catch {
                shell.shopDotsStatus.textContent = "Dots update failed (forbidden or network).";
            }
        };
        const mk = (text: string, mode: "set" | "add" | "subtract", className = "btn adminRecordsBtn") => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = className;
            btn.textContent = text;
            btn.onclick = () => void run(mode, text);
            return btn;
        };
        controls.appendChild(amount);
        controls.appendChild(mk("Set", "set", "btn green adminRecordsBtn"));
        controls.appendChild(mk("Add", "add"));
        controls.appendChild(mk("Subtract", "subtract"));
        shell.shopDotsBalance.appendChild(controls);
    }

    function renderShopDotsInventory(detail: AdminPlayerStatsDetailApi) {
        const purchases = Array.isArray(detail.purchases) ? detail.purchases : [];
        shell.shopDotsInventory.innerHTML = "";
        if (!purchases.length) {
            const p = document.createElement("p");
            p.className = "accessHint";
            p.textContent = "No owned shop items.";
            shell.shopDotsInventory.appendChild(p);
            return;
        }
        const tbl = document.createElement("table");
        tbl.className = "lbTable adminRecordsTable";
        const thead = document.createElement("thead");
        const hr = document.createElement("tr");
        for (const h of ["Item", "Category", "Paid", "Equipped", "Actions"]) {
            const th = document.createElement("th");
            th.textContent = h;
            hr.appendChild(th);
        }
        thead.appendChild(hr);
        tbl.appendChild(thead);
        const tb = document.createElement("tbody");
        for (const item of purchases) {
            const tr = document.createElement("tr");
            for (const txt of [
                item.name,
                item.category,
                item.pricePaid > 0 ? `${item.pricePaid.toLocaleString()} Dots` : "—",
                item.equipped ? "Yes" : "No",
            ]) {
                const td = document.createElement("td");
                td.textContent = txt;
                tr.appendChild(td);
            }
            const tdAct = document.createElement("td");
            tdAct.className = "adminRecordsActions";
            const removeOne = (refund: boolean) => {
                const refundLabel = refund
                    ? ` and refund ${item.pricePaid.toLocaleString()} Dots`
                    : " without refund";
                if (
                    !confirm(
                        `Remove "${item.name}" from ${detail.displayName || "this player"}${refundLabel}?`,
                    )
                ) {
                    return;
                }
                void (async () => {
                    try {
                        const res = await removeAdminPlayerPurchase(detail.userId, item.cosmeticId, refund);
                        await loadShopDotsDetail(detail.userId);
                        shell.shopDotsStatus.textContent = refund
                            ? `Removed item (refunded ${res.refunded.toLocaleString()} Dots).`
                            : "Removed item (no refund).";
                    } catch {
                        shell.shopDotsStatus.textContent = "Remove item failed (forbidden or network).";
                    }
                })();
            };
            addStatActionBtn(tdAct, "Remove", () => removeOne(false));
            if (item.pricePaid > 0) {
                addStatActionBtn(tdAct, "Remove + refund", () => removeOne(true));
            }
            tr.appendChild(tdAct);
            tb.appendChild(tr);
        }
        tbl.appendChild(tb);
        shell.shopDotsInventory.appendChild(tbl);
    }

    async function loadShopDotsDetail(userId: string) {
        shopDotsActiveUserId = userId;
        shell.shopDotsDetail.classList.remove("hidden");
        shell.shopDotsStatus.textContent = "Loading…";
        try {
            const detail = await fetchAdminPlayerStats(userId);
            shell.shopDotsDetailTitle.textContent = detail.email
                ? `${detail.displayName || "?"} — ${detail.email}`
                : detail.displayName || userId;
            shell.shopDotsStatus.textContent = "";
            renderShopDotsBalance(detail);
            renderShopDotsInventory(detail);
        } catch {
            shell.shopDotsStatus.textContent = "Could not load player (forbidden or network).";
            shell.shopDotsDetail.classList.add("hidden");
        }
    }

    async function runShopDotsSearch() {
        if (!hasShopDotsAccess()) return;
        const q = shell.shopDotsSearch.value.trim();
        if (q.length < 2) {
            shell.shopDotsStatus.textContent = "Enter at least 2 characters.";
            return;
        }
        shell.shopDotsStatus.textContent = "Searching…";
        shell.shopDotsCandidates.innerHTML = "";
        shell.shopDotsDetail.classList.add("hidden");
        try {
            const result = await lookupAdminPlayerStats(q);
            if (result.kind === "ambiguous") {
                shell.shopDotsStatus.textContent = "Multiple matches — pick one:";
                const tbl = document.createElement("table");
                tbl.className = "lbTable adminRecordsTable";
                const thead = document.createElement("thead");
                const hr = document.createElement("tr");
                for (const h of ["Username", "Email", ""]) {
                    const th = document.createElement("th");
                    th.textContent = h;
                    hr.appendChild(th);
                }
                thead.appendChild(hr);
                tbl.appendChild(thead);
                const tb = document.createElement("tbody");
                for (const c of result.candidates) {
                    const tr = document.createElement("tr");
                    for (const txt of [c.displayName || "—", c.email || "—"]) {
                        const td = document.createElement("td");
                        td.textContent = txt;
                        tr.appendChild(td);
                    }
                    const tdAct = document.createElement("td");
                    tdAct.className = "adminRecordsActions";
                    addStatActionBtn(tdAct, "Open", () => {
                        shell.shopDotsCandidates.innerHTML = "";
                        void loadShopDotsDetail(c.userId);
                    });
                    tr.appendChild(tdAct);
                    tb.appendChild(tr);
                }
                tbl.appendChild(tb);
                shell.shopDotsCandidates.appendChild(tbl);
                return;
            }
            if (result.kind === "found") {
                await loadShopDotsDetail(result.detail.userId);
                return;
            }
            shell.shopDotsStatus.textContent = "No player found.";
        } catch {
            shell.shopDotsStatus.textContent = "Search failed (forbidden or network).";
        }
    }

    function renderSimpleDeleteTable(
        wrap: HTMLElement,
        headers: string[],
        rows: Array<{ cells: string[]; onDelete: () => void }>,
        emptyText: string,
    ) {
        wrap.innerHTML = "";
        if (!rows.length) {
            const p = document.createElement("p");
            p.className = "accessHint";
            p.textContent = emptyText;
            wrap.appendChild(p);
            return;
        }
        const tbl = document.createElement("table");
        tbl.className = "lbTable adminRecordsTable";
        const thead = document.createElement("thead");
        const hr = document.createElement("tr");
        for (const h of [...headers, "Actions"]) {
            const th = document.createElement("th");
            th.textContent = h;
            hr.appendChild(th);
        }
        thead.appendChild(hr);
        tbl.appendChild(thead);
        const tb = document.createElement("tbody");
        for (const row of rows) {
            const tr = document.createElement("tr");
            for (const txt of row.cells) {
                const td = document.createElement("td");
                td.textContent = txt;
                tr.appendChild(td);
            }
            const tdAct = document.createElement("td");
            tdAct.className = "adminRecordsActions";
            addStatActionBtn(tdAct, "Delete", row.onDelete);
            tr.appendChild(tdAct);
            tb.appendChild(tr);
        }
        tbl.appendChild(tb);
        wrap.appendChild(tbl);
    }

    async function loadPlayerStatsDetail(userId: string) {
        shell.playerStatsStatus.textContent = "Loading…";
        try {
            const detail = await fetchAdminPlayerStats(userId);
            shell.playerStatsStatus.textContent = detail.email ? `${detail.displayName || "?"} — ${detail.email}` : "";
            renderPlayerStatsCore(detail);
            renderSimpleDeleteTable(
                shell.playerStatsAchievements,
                ["Achievement"],
                detail.achievements.map((a) => ({
                    cells: [a.title],
                    onDelete: () => {
                        if (!confirm(`Remove achievement "${a.title}" for this player?`)) return;
                        void (async () => {
                            try {
                                await deleteAdminPlayerAchievement(userId, a.id);
                                await notifyRecordsChanged(userId);
                                await loadPlayerStatsDetail(userId);
                            } catch {
                                shell.playerStatsStatus.textContent = "Delete failed (forbidden or network).";
                            }
                        })();
                    },
                })),
                "No unlocked achievements.",
            );
            renderSimpleDeleteTable(
                shell.playerStatsRecords,
                ["Mode", "Level", "Score / Time", "Device", "When"],
                detail.leaderboardRecords.map((r) => ({
                    cells: [
                        r.gameMode,
                        r.speedrunLevel != null ? String(r.speedrunLevel) : "—",
                        r.gameMode === "speedrun" ? formatSpeedrunTime(Number(r.value) || 0) : String(r.value),
                        r.device === "mobile" ? "Mobile" : r.device === "pc" ? "PC" : "—",
                        new Date(r.createdAt).toLocaleString(),
                    ],
                    onDelete: () => {
                        if (!confirm("Delete this leaderboard record permanently?")) return;
                        void (async () => {
                            try {
                                const { userId: uid } = await deleteAdminLeaderboardEntry(r.id);
                                if (uid) await notifyRecordsChanged(uid);
                                await loadPlayerStatsDetail(userId);
                                if (recordsOpen && !playerStatsOpen) void loadRecords();
                            } catch {
                                shell.playerStatsStatus.textContent = "Delete failed (forbidden or network).";
                            }
                        })();
                    },
                })),
                "No leaderboard records.",
            );
            renderSimpleDeleteTable(
                shell.playerStatsSynced,
                ["Record", "Value"],
                detail.syncedRecords.map((r) => ({
                    cells: [
                        r.label,
                        r.kind === "speedrun" || r.kind === "sandboxSpeedrun"
                            ? formatSpeedrunTime(Number(r.value) || 0)
                            : String(r.value),
                    ],
                    onDelete: () => {
                        if (!confirm(`Delete synced record "${r.label}"?`)) return;
                        void (async () => {
                            try {
                                await deleteAdminPlayerSyncedRecord(userId, r.kind, r.key);
                                await notifyRecordsChanged(userId);
                                await loadPlayerStatsDetail(userId);
                            } catch {
                                shell.playerStatsStatus.textContent = "Delete failed (forbidden or network).";
                            }
                        })();
                    },
                })),
                "No extra synced records.",
            );
        } catch {
            shell.playerStatsStatus.textContent = "Could not load player stats.";
        }
    }

    function formatTimeoutRemaining(untilIso: string | null): string {
        if (!untilIso) return "—";
        const ms = Date.parse(untilIso) - Date.now();
        if (!Number.isFinite(ms) || ms <= 0) return "Expired";
        const totalSec = Math.ceil(ms / 1000);
        const days = Math.floor(totalSec / 86400);
        const hours = Math.floor((totalSec % 86400) / 3600);
        const mins = Math.floor((totalSec % 3600) / 60);
        const secs = totalSec % 60;
        if (days > 0) return `${days}d ${hours}h ${mins}m`;
        if (hours > 0) return `${hours}h ${mins}m`;
        if (mins > 0) return `${mins}m ${secs}s`;
        return `${secs}s`;
    }

    function timeoutDurationInput(): string {
        return shell.timeoutMinutes.value.trim() || "60m";
    }

    function parseTimeoutInput(input: string): string | null {
        return parseTimeoutDuration(input) != null ? input.trim() : null;
    }

    type TimeoutReduceContext = {
        userId: string;
        label: string;
        timedOutUntil: string;
        onChanged: () => Promise<void>;
    };

    let timeoutReduceContext: TimeoutReduceContext | null = null;

    function closeTimeoutReduceModal() {
        timeoutReduceContext = null;
        shell.timeoutReduceModal.classList.add("hidden");
        shell.timeoutReduceError.textContent = "";
    }

    function openTimeoutReduceModal(ctx: TimeoutReduceContext) {
        timeoutReduceContext = ctx;
        shell.timeoutReduceUser.textContent = `Player: ${ctx.label}`;
        shell.timeoutReduceCurrent.textContent = `Time left: ${formatTimeoutRemaining(ctx.timedOutUntil)}`;
        shell.timeoutReduceMode.value = "reduce";
        shell.timeoutReduceDuration.value = "";
        shell.timeoutReduceError.textContent = "";
        shell.timeoutReduceModal.classList.remove("hidden");
        shell.timeoutReduceDuration.focus();
    }

    async function applyTimeoutReduceModal() {
        const ctx = timeoutReduceContext;
        if (!ctx) return;
        const durationStr = shell.timeoutReduceDuration.value.trim();
        const amountMs = parseTimeoutDuration(durationStr);
        if (amountMs == null) {
            shell.timeoutReduceError.textContent = "Invalid duration. Use s, m, h, or d (e.g. 30s, 5m, 2h, 5d).";
            return;
        }
        shell.timeoutReduceError.textContent = "";
        const mode = shell.timeoutReduceMode.value;
        try {
            if (mode === "reduce") {
                await applyAdminUserTimeout(ctx.userId, durationStr, "reduce");
            } else {
                const remainingMs = Date.parse(ctx.timedOutUntil) - Date.now();
                if (!Number.isFinite(remainingMs) || remainingMs <= 0) {
                    shell.timeoutReduceError.textContent = "Timeout already expired.";
                    return;
                }
                if (amountMs <= 1000) {
                    await clearAdminUserTimeout(ctx.userId);
                } else {
                    const delta = amountMs - remainingMs;
                    if (Math.abs(delta) < 1000) {
                        closeTimeoutReduceModal();
                        return;
                    }
                    if (delta > 0) {
                        await applyAdminUserTimeout(ctx.userId, msToDurationToken(delta), "extend");
                    } else {
                        await applyAdminUserTimeout(ctx.userId, msToDurationToken(-delta), "reduce");
                    }
                }
            }
            closeTimeoutReduceModal();
            await ctx.onChanged();
        } catch {
            shell.timeoutReduceError.textContent = "Timeout action failed (forbidden or network).";
            shell.timeoutStatus.textContent = "Timeout action failed (forbidden or network).";
            shell.timeoutActiveStatus.textContent = "Timeout action failed (forbidden or network).";
        }
    }

    shell.timeoutReduceCancelBtn.onclick = closeTimeoutReduceModal;
    shell.timeoutReduceApplyBtn.onclick = () => void applyTimeoutReduceModal();
    shell.timeoutReduceModal.addEventListener("click", (ev) => {
        if (ev.target === shell.timeoutReduceModal) closeTimeoutReduceModal();
    });
    shell.timeoutReduceDuration.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") void applyTimeoutReduceModal();
        if (ev.key === "Escape") closeTimeoutReduceModal();
    });

    type DeleteRecordsContext = {
        userId: string;
        displayName: string;
        gameMode: string;
        defaultLevel: number | null;
    };

    let deleteRecordsContext: DeleteRecordsContext | null = null;

    function closeDeleteRecordsModal() {
        deleteRecordsContext = null;
        shell.deleteRecordsModal.classList.add("hidden");
        shell.deleteRecordsError.textContent = "";
        shell.deleteRecordsLevelList.replaceChildren();
    }

    async function performDeleteAllRecords(userId: string, gameMode: string, speedrunLevel?: number) {
        try {
            const { userId: uid } = await deleteAllAdminLeaderboardEntries(userId, gameMode, speedrunLevel);
            await notifyRecordsChanged(uid);
            await loadRecords();
            if (activePlayerStatsUserId === uid) await loadPlayerStatsDetail(uid);
            closeDeleteRecordsModal();
        } catch {
            if (deleteRecordsContext) {
                shell.deleteRecordsError.textContent = "Delete failed (forbidden or network).";
            } else {
                shell.recordsStatus.textContent = "Delete all failed (forbidden or network).";
            }
        }
    }

    function openDeleteRecordsModal(ctx: DeleteRecordsContext) {
        deleteRecordsContext = ctx;
        shell.deleteRecordsUser.textContent = `Player: ${ctx.displayName}`;
        shell.deleteRecordsMode.textContent = `Mode: ${ctx.gameMode}`;
        shell.deleteRecordsError.textContent = "";
        shell.deleteRecordsLevelList.replaceChildren();
        const levelOpts = Array.from(shell.recordsSpeedrunLevel.options);
        if (!levelOpts.length) {
            const fallback = document.createElement("button");
            fallback.type = "button";
            fallback.className = "btn";
            fallback.textContent = "Level 1";
            fallback.onclick = () => {
                if (!confirm(`Delete all speedrun records for ${ctx.displayName} on level 1?`)) return;
                void performDeleteAllRecords(ctx.userId, ctx.gameMode, 1);
            };
            shell.deleteRecordsLevelList.appendChild(fallback);
        } else {
            for (const opt of levelOpts) {
                const lv = Math.floor(Number(opt.value) || 0);
                if (!Number.isInteger(lv) || lv < 1) continue;
                const btn = document.createElement("button");
                btn.type = "button";
                btn.className = "btn";
                if (ctx.defaultLevel === lv) btn.classList.add("green");
                btn.textContent = opt.textContent || `Level ${lv}`;
                btn.onclick = () => {
                    if (!confirm(`Delete all speedrun records for ${ctx.displayName} on level ${lv}?`)) return;
                    void performDeleteAllRecords(ctx.userId, ctx.gameMode, lv);
                };
                shell.deleteRecordsLevelList.appendChild(btn);
            }
        }
        shell.deleteRecordsModal.classList.remove("hidden");
    }

    shell.deleteRecordsCancelBtn.onclick = closeDeleteRecordsModal;
    shell.deleteRecordsAllLevelsBtn.onclick = () => {
        const ctx = deleteRecordsContext;
        if (!ctx) return;
        if (
            !confirm(
                `Delete ALL ${ctx.gameMode} records for ${ctx.displayName} on every level? This clears ranking and Best Score for the whole mode.`,
            )
        ) {
            return;
        }
        void performDeleteAllRecords(ctx.userId, ctx.gameMode);
    };
    shell.deleteRecordsModal.addEventListener("click", (ev) => {
        if (ev.target === shell.deleteRecordsModal) closeDeleteRecordsModal();
    });

    function buildTimeoutActionButtons(
        u: {
            id: string;
            displayName: string | null;
            email: string | null;
            timedOut: boolean;
            timedOutUntil: string | null;
        },
        onChanged: () => Promise<void>,
    ): HTMLElement {
        const tdAct = document.createElement("td");
        tdAct.className = "adminRecordsActions";
        const label = u.displayName || u.email || u.id;
        const durationStr = timeoutDurationInput();
        const selfBlocked = !!shell.currentUserId && u.id === shell.currentUserId;

        const addBtn = (text: string, action: AdminTimeoutAction | "remove", className = "btn adminRecordsBtn") => {
            const btn = document.createElement("button");
            btn.type = "button";
            btn.className = className;
            btn.textContent = text;
            btn.disabled = selfBlocked && action !== "reduce";
            btn.onclick = () => {
                if (action === "reduce") {
                    if (!u.timedOutUntil) return;
                    openTimeoutReduceModal({
                        userId: u.id,
                        label,
                        timedOutUntil: u.timedOutUntil,
                        onChanged,
                    });
                    return;
                }
                const parsed = parseTimeoutInput(durationStr);
                if (!parsed && action !== "remove") {
                    shell.timeoutStatus.textContent = "Invalid timeout duration. Use s, m, h, or d.";
                    shell.timeoutActiveStatus.textContent = "Invalid timeout duration. Use s, m, h, or d.";
                    return;
                }
                const durLabel = parsed ? formatTimeoutDurationLabel(parsed) : "";
                const prompts: Record<string, string> = {
                    apply: `Timeout ${label} for ${durLabel}?`,
                    extend: `Add ${durLabel} to ${label}'s timeout?`,
                    remove: `Remove timeout for ${label}? They can play normally again.`,
                };
                if (!confirm(prompts[action])) return;
                void (async () => {
                    try {
                        if (action === "remove") {
                            await clearAdminUserTimeout(u.id);
                        } else {
                            await applyAdminUserTimeout(u.id, parsed!, action);
                        }
                        await onChanged();
                    } catch {
                        shell.timeoutStatus.textContent = "Timeout action failed (forbidden or network).";
                        shell.timeoutActiveStatus.textContent = "Timeout action failed (forbidden or network).";
                    }
                })();
            };
            tdAct.appendChild(btn);
        };

        if (u.timedOut) {
            addBtn("Remove timeout", "remove");
            addBtn("Extend", "extend");
            addBtn("Reduce", "reduce");
        } else {
            addBtn("Apply timeout", "apply");
        }
        const manageBtn = document.createElement("button");
        manageBtn.type = "button";
        manageBtn.className = "btn adminRecordsBtn";
        manageBtn.textContent = "Manage";
        manageBtn.onclick = () => void openPlayerStatsScreen(u.id, u.displayName || u.email || u.id);
        tdAct.appendChild(manageBtn);
        return tdAct;
    }

    function renderTimeoutUsersTable(
        wrap: HTMLElement,
        rows: Awaited<ReturnType<typeof fetchAdminTimeoutUsers>>,
        onChanged: () => Promise<void>,
    ) {
        wrap.innerHTML = "";
        if (!rows.length) return;
        const tbl = document.createElement("table");
        tbl.className = "lbTable adminRecordsTable";
        const thead = document.createElement("thead");
        const hr = document.createElement("tr");
        ["Name", "Email", "Time left", "Actions"].forEach((h) => {
            const th = document.createElement("th");
            th.textContent = h;
            hr.appendChild(th);
        });
        thead.appendChild(hr);
        tbl.appendChild(thead);
        const tb = document.createElement("tbody");
        for (const u of rows) {
            const tr = document.createElement("tr");
            const timeLeft = u.timedOut ? formatTimeoutRemaining(u.timedOutUntil) : "—";
            for (const txt of [u.displayName || "—", u.email || "—", timeLeft]) {
                const td = document.createElement("td");
                td.textContent = txt;
                tr.appendChild(td);
            }
            tr.appendChild(buildTimeoutActionButtons(u, onChanged));
            tb.appendChild(tr);
        }
        tbl.appendChild(tb);
        wrap.appendChild(tbl);
    }

    async function loadActiveTimeoutUsers() {
        shell.timeoutActiveTableWrap.innerHTML = "";
        shell.timeoutActiveStatus.textContent = "Loading…";
        try {
            const rows = await fetchAdminActiveTimeoutUsers();
            if (!rows.length) {
                shell.timeoutActiveStatus.textContent = "No active timeouts.";
                return;
            }
            shell.timeoutActiveStatus.textContent = `${rows.length} active timeout(s).`;
            renderTimeoutUsersTable(shell.timeoutActiveTableWrap, rows, async () => {
                await loadActiveTimeoutUsers();
                if (shell.timeoutSearch.value.trim().length >= 2) await loadTimeoutUsers();
            });
        } catch {
            shell.timeoutActiveStatus.textContent = "Could not load active timeouts.";
        }
    }

    async function loadTimeoutUsers() {
        shell.timeoutTableWrap.innerHTML = "";
        shell.timeoutStatus.textContent = "Loading…";
        const q = shell.timeoutSearch.value.trim();
        if (q.length < 2) {
            shell.timeoutStatus.textContent = "Enter at least 2 characters to search.";
            return;
        }
        try {
            const rows = await fetchAdminTimeoutUsers(q);
            if (!rows.length) {
                shell.timeoutStatus.textContent = "No users match.";
                return;
            }
            shell.timeoutStatus.textContent = `${rows.length} user(s).`;
            renderTimeoutUsersTable(shell.timeoutTableWrap, rows, async () => {
                await loadTimeoutUsers();
                await loadActiveTimeoutUsers();
            });
        } catch {
            shell.timeoutStatus.textContent = "Could not load (admin session / database).";
        }
    }

    shell.timeoutSearchBtn.onclick = () => void loadTimeoutUsers();
    shell.timeoutActiveRefreshBtn.onclick = () => void loadActiveTimeoutUsers();

    async function notifyRecordsChanged(userId: string | null) {
        if (userId) await shell.onPlayerRecordsChanged?.(userId);
    }

    async function loadRecords() {
        shell.recordsTableWrap.innerHTML = "";
        shell.recordsStatus.textContent = "Loading…";
        syncSpeedrunFilterRow();
        try {
            const mode = shell.recordsMode.value;
            const q: Parameters<typeof fetchAdminLeaderboardEntries>[0] = {
                search: shell.recordsSearch.value.trim() || undefined,
                mode: mode || undefined,
            };
            if (mode === "speedrun") {
                const lv = Math.floor(Number(shell.recordsSpeedrunLevel.value) || 1);
                q.speedrunLevel = lv;
            }
            const rows = await fetchAdminLeaderboardEntries(q);
            if (!rows.length) {
                shell.recordsStatus.textContent = "No records match.";
                return;
            }
            shell.recordsStatus.textContent = `${rows.length} best record(s) per player for this filter.`;
            const tbl = document.createElement("table");
            tbl.className = "lbTable adminRecordsTable";
            const thead = document.createElement("thead");
            const hr = document.createElement("tr");
            ["Name", "Mode", "Level", "Device", "When", "Score / Time", "Actions"].forEach((h) => {
                const th = document.createElement("th");
                th.textContent = h;
                hr.appendChild(th);
            });
            thead.appendChild(hr);
            tbl.appendChild(thead);
            const tb = document.createElement("tbody");
            for (const it of rows) {
                const tr = document.createElement("tr");
                const modeLabel = it.gameMode;
                const lvlCell =
                    it.gameMode === "speedrun" && it.speedrunLevel != null ? String(it.speedrunLevel) : "—";
                const valCell =
                    it.gameMode === "speedrun" ? formatSpeedrunTime(Number(it.value) || 0) : String(it.value);
                const deviceLabel = it.device === "mobile" ? "Mobile" : it.device === "pc" ? "PC" : "—";
                const del = document.createElement("button");
                del.type = "button";
                del.className = "btn adminRecordsBtn";
                del.textContent = "Delete Record";
                del.onclick = () => {
                    if (!confirm("Delete this leaderboard record permanently?")) return;
                    void (async () => {
                        try {
                            const { userId } = await deleteAdminLeaderboardEntry(it.id);
                            await notifyRecordsChanged(userId);
                            await loadRecords();
                        } catch {
                            shell.recordsStatus.textContent = "Delete failed (forbidden or network).";
                        }
                    })();
                };
                const delAll = document.createElement("button");
                delAll.type = "button";
                delAll.className = "btn adminRecordsBtn";
                delAll.textContent = "Delete all records";
                delAll.disabled = !it.userId;
                delAll.title = it.userId
                    ? MODERATION_MODES_WITH_LEVELS.has(it.gameMode)
                        ? `Remove ${it.gameMode} records for this player (choose level)`
                        : `Remove every ${it.gameMode} record for this player`
                    : "Guest records can only be deleted one at a time";
                delAll.onclick = () => {
                    if (!it.userId) return;
                    if (MODERATION_MODES_WITH_LEVELS.has(it.gameMode)) {
                        openDeleteRecordsModal({
                            userId: it.userId,
                            displayName: it.displayName || "?",
                            gameMode: it.gameMode,
                            defaultLevel: it.speedrunLevel,
                        });
                        return;
                    }
                    if (
                        !confirm(
                            `Delete ALL ${it.gameMode} records for ${it.displayName}? This clears ranking and Best Score for that mode.`,
                        )
                    ) {
                        return;
                    }
                    void performDeleteAllRecords(it.userId, it.gameMode);
                };
                const cells = [
                    it.displayName || "?",
                    modeLabel,
                    lvlCell,
                    deviceLabel,
                    new Date(it.createdAt).toLocaleString(),
                    valCell,
                ];
                for (const txt of cells) {
                    const td = document.createElement("td");
                    td.textContent = txt;
                    tr.appendChild(td);
                }
                const tdAct = document.createElement("td");
                tdAct.className = "adminRecordsActions";
                tdAct.appendChild(del);
                tdAct.appendChild(delAll);
                tr.appendChild(tdAct);
                tb.appendChild(tr);
            }
            tbl.appendChild(tb);
            shell.recordsTableWrap.appendChild(tbl);
        } catch {
            shell.recordsStatus.textContent = "Could not load (admin session / database).";
        }
    }

    shell.recordsRefresh.onclick = () => {
        void loadRecords();
        void resolveRecordsSearchUser();
    };

    shell.btnReturn.onclick = () => {
        if (playerStatsOpen) {
            closePlayerStatsScreen();
            return;
        }
        if (!shell.panelPracticeEditor.classList.contains("hidden")) {
            shell.panelPracticeEditor.classList.add("hidden");
            standalonePracticeEditorActive = false;
            if (practiceEditorFromLevels) {
                shell.panelPracticeLevels.classList.remove("hidden");
                practiceLevelsOpen = true;
                practiceLevelsApi?.refresh();
            }
            practiceEditorFromLevels = false;
            syncNavHighlight();
            return;
        }
        if (!shell.panelEditor.classList.contains("hidden")) {
            shell.panelEditor.classList.add("hidden");
            standaloneEditorActive = false;
            if (editorFromLevels) {
                shell.panelLevels.classList.remove("hidden");
                levelsOpen = true;
                levelsApi?.refresh();
            }
            editorFromLevels = false;
            syncNavHighlight();
            return;
        }
        if (recordsOpen || passwordRecoveryOpen || levelsOpen || practiceLevelsOpen || administratorsOpen) {
            hideAllPanels();
            return;
        }
        shell.screen.classList.add("hidden");
        document.getElementById("startScreen")?.classList.remove("hidden");
        document.getElementById("returnBtn")?.classList.add("hidden");
        shell.onVisibilityChange?.();
        shell.onAdminClosed?.();
    };

    hideAllPanels();

    return {
        open() {
            claimExclusiveUi("adminPanelScreen");
            document.getElementById("startScreen")?.classList.add("hidden");
            document.body.classList.remove("screenEnterMotion");
            document.getElementById("returnBtn")?.classList.remove("hidden");
            shell.screen.classList.remove("hidden");
            shell.panelEditor.classList.add("hidden");
            hideAllPanels();
            shell.recordsTableWrap.innerHTML = "";
            shell.recordsStatus.textContent = "";
            shell.onVisibilityChange?.();
        },
        destroy() {
            editorApi?.destroy();
            editorApi = null;
            levelsApi?.destroy();
            levelsApi = null;
            penaltyEditorApi?.destroy();
            penaltyEditorApi = null;
            penaltyLevelsApi?.destroy();
            penaltyLevelsApi = null;
            practiceEditorApi?.destroy();
            practiceEditorApi = null;
            practiceLevelsApi?.destroy();
            practiceLevelsApi = null;
        },
        refillSpeedrunLevelFilter(maxLevel: number) {
            const sel = shell.recordsSpeedrunLevel;
            const cur = sel.value;
            sel.replaceChildren();
            const n = Math.max(1, maxLevel);
            for (let i = 1; i <= n; i++) {
                const o = document.createElement("option");
                o.value = String(i);
                o.textContent = `Level ${i}`;
                sel.appendChild(o);
            }
            sel.value = clampStrNum(cur, 1, n);
        },
        restoreEditorAfterPlaytest() {
            shell.panelRecords.classList.add("hidden");
            shell.panelPasswordRecovery.classList.add("hidden");
            shell.panelLevels.classList.add("hidden");
            shell.panelPenaltyLevels.classList.add("hidden");
            shell.panelPracticeLevels.classList.add("hidden");
            shell.panelPracticeEditor.classList.add("hidden");
            shell.panelWall.classList.add("hidden");
            shell.panelAchievements.classList.add("hidden");
            shell.panelAdministrators.classList.add("hidden");
            recordsOpen = false;
            passwordRecoveryOpen = false;
            levelsOpen = false;
            penaltyLevelsOpen = false;
            practiceLevelsOpen = false;
            wallOpen = false;
            achievementsOpen = false;
            administratorsOpen = false;
            editorFromLevels = false;
            penaltyEditorFromLevels = false;
            practiceEditorFromLevels = false;
            standaloneEditorActive = true;
            standalonePenaltyEditorActive = false;
            standalonePracticeEditorActive = false;
            shell.panelEditor.classList.remove("hidden");
            shell.panelPenaltyEditor.classList.add("hidden");
            syncNavHighlight();
            ensureEditor().refitCanvas();
        },
        restorePenaltyEditorAfterPlaytest() {
            shell.panelRecords.classList.add("hidden");
            shell.panelPasswordRecovery.classList.add("hidden");
            shell.panelLevels.classList.add("hidden");
            shell.panelEditor.classList.add("hidden");
            shell.panelPenaltyLevels.classList.add("hidden");
            shell.panelPracticeLevels.classList.add("hidden");
            shell.panelPracticeEditor.classList.add("hidden");
            shell.panelWall.classList.add("hidden");
            shell.panelAchievements.classList.add("hidden");
            shell.panelAdministrators.classList.add("hidden");
            recordsOpen = false;
            passwordRecoveryOpen = false;
            levelsOpen = false;
            penaltyLevelsOpen = false;
            practiceLevelsOpen = false;
            wallOpen = false;
            achievementsOpen = false;
            administratorsOpen = false;
            editorFromLevels = false;
            penaltyEditorFromLevels = false;
            practiceEditorFromLevels = false;
            standaloneEditorActive = false;
            standalonePenaltyEditorActive = true;
            standalonePracticeEditorActive = false;
            shell.panelPenaltyEditor.classList.remove("hidden");
            shell.panelEditor.classList.add("hidden");
            syncNavHighlight();
            ensurePenaltyEditor().refitCanvas();
        },
        restorePracticeEditorAfterPlaytest() {
            shell.panelRecords.classList.add("hidden");
            shell.panelPasswordRecovery.classList.add("hidden");
            shell.panelLevels.classList.add("hidden");
            shell.panelEditor.classList.add("hidden");
            shell.panelPenaltyLevels.classList.add("hidden");
            shell.panelPenaltyEditor.classList.add("hidden");
            shell.panelPracticeLevels.classList.add("hidden");
            shell.panelWall.classList.add("hidden");
            shell.panelAchievements.classList.add("hidden");
            shell.panelAdministrators.classList.add("hidden");
            recordsOpen = false;
            passwordRecoveryOpen = false;
            levelsOpen = false;
            penaltyLevelsOpen = false;
            practiceLevelsOpen = false;
            wallOpen = false;
            achievementsOpen = false;
            administratorsOpen = false;
            editorFromLevels = false;
            penaltyEditorFromLevels = false;
            practiceEditorFromLevels = false;
            standaloneEditorActive = false;
            standalonePenaltyEditorActive = false;
            standalonePracticeEditorActive = true;
            shell.panelPracticeEditor.classList.remove("hidden");
            syncNavHighlight();
            ensurePracticeEditor().refitCanvas();
        },
    };
}

function clampStrNum(s: string, lo: number, hi: number): string {
    const v = Math.floor(Number(s) || lo);
    return String(Math.max(lo, Math.min(hi, v)));
}
